import { afterNextRender, Component, computed, ElementRef, Injector, NgZone, OnDestroy, OnInit, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { ListRange } from "@angular/cdk/collections";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { CdkVirtualScrollViewport } from "@angular/cdk/scrolling";
import { Sort } from "@angular/material/sort";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { catchError, concatMap, debounceTime, EMPTY, defer, filter, finalize, forkJoin, from, map, mergeMap, Observable, of, Subject, switchMap, takeUntil, tap, timer, toArray } from "rxjs";
import {
    AssessmentStatusEnum,
    RiskRegisterAssessmentColorsEnum,
    RiskRegisterAssessmentIconsEnum,
    RiskRegisterAssessmentStatusEnum,
} from "src/app/enums/assessmentStatus.enum";
import {
    AssessmentMethodEnum,
    RiskAssessment,
    RiskRegister,
    RiskLevel,
    LikelihoodColorLevel,
    TableExtendedRiskRegister,
} from "src/app/models/risk-assessment.model";
import { CommonService } from "src/app/services/common.service";
import { RiskAssessmentService } from "src/app/services/risk-assessment.service";
import { RiskRegisterService } from "src/app/services/risk-register.service";
import { AgentService } from "src/app/services/agent.service";
import { Agent } from "src/app/models/agent.model";
import { TenantRiskService } from "src/app/services/tenantRisk.service";
import { TimeZoneService } from "src/app/services/timeZone.service";
import { ToastService } from "src/app/services/toast.service";
import { Router } from "@angular/router";
import { MatRadioChange } from "@angular/material/radio";
import { MatMenu } from "@angular/material/menu";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import {
    BulkDeleteConfirmationDialogComponent,
    BulkDeleteDialogData,
} from "src/app/modals/bulk-delete-confirmation-dialog/bulk-delete-confirmation-dialog.component";
import { RiskRegistersReportPopupComponent } from "src/app/common-components/risk-registers-report-popup/risk-registers-report-popup.component";
import { getRiskColor, getLikelihoodColor, getRiskLevelName } from "src/app/_helpers/risk-color.helper";
import { getEnumLookup, getNamedEnum } from "src/app/_helpers/enum-helper";
import { Lookup, SelectableLookup } from "src/app/models/lookup.model";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import { ILookup } from "src/app/shared/form/lookup.model";
import { TranslateService } from "src/app/services/translate.service";
import { OelCorrectionMethod } from "src/app/enums/oelCorrectionMethod.enum";
import { JurisdictionService } from "src/app/services/jurisdiction.service";
import { OelTypeService } from "src/app/services/oel-type.service";
import { SessionPreferencesService } from "src/app/services/session-preferences.service";
import { RiskRegisterViewMode } from "src/app/enums/riskRegisterViewMode.enum";
import { CensoredDataMethod, CENSORED_DATA_METHOD_DISPLAY_LABELS } from "src/app/enums/censoredDataMethod.enum";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { GridPermissions, gridToPermissionFlags } from "src/app/shared/grid/core-grid/gridPermissions.enum";
import { PermissionService } from "src/app/services/permission.service";
import { GridViewService } from "src/app/services/grid-view.service";
import { GridView } from "src/app/models/gridView.model";
import { saveGridView } from "src/app/shared/grid/grid-view-default.helper";
import { GridViewType } from "src/app/enums/gridViewType.enum";
import { DisplayGridColumnSettings } from "src/app/models/gridColumnSettings.model";
import { GridSettingsExtraTab } from "src/app/models/gridSettingsExtraTab.model";
import { RiskRegisterAgentToggle, RiskRegisterMatrixSort } from "src/app/models/risk-register-matrix.model";
import { JurisdictionRiskConfigService } from "src/app/services/Jurisdiction-risk-config.service";
import { SegProfileService } from "src/app/services/seg-profile.service";
import { SegJobRollupResponse } from "src/app/models/seg-profile-summary.model";
import { SegJobRoleSource } from "src/app/services/template-mappers/entities/risk-register-seg-group.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    APPROVED_REGISTER_BLOCKED_TEXT,
    APPROVED_REGISTER_BULK_HINT_TEXT,
    isApprovedRiskRegisterStatus,
} from "src/app/shared/utils/risk-register-status.util";
import { resolveExposureBandLabel } from "src/app/shared/utils/risk-level-parsing.util";
import { resolveRiskConfig } from "src/app/shared/utils/risk-config-resolution.util";
import { ConsequenceRating } from "src/app/models/jurisdictionRiskConfig.model";
import { RiskLegendItem } from "src/app/models/risk-config-legend.model";
import {
    buildSegRows,
    mergeSegRows,
    moveInSharedOrder,
    orderAgentsLike,
    rebuildRowCells,
    replaceSegRows,
    SegPageRowInfo,
    toAgentColumns,
    visibleAgentColumns,
} from "./risk-register-matrix.util";
import {
    buildViewColumns,
    filtersToSettings,
    freezableColumns,
    frozenColumns,
    isAgentColumnSort,
    keepLockedColumnsVisible,
    needsReload,
    readPageSettings,
    readViewState,
    resolveSort,
    RISK_REGISTER_DEFAULT_FROZEN_COLUMN_COUNT,
    RISK_REGISTER_VIEW_OBJECT_ID,
    riskRegisterGridId,
    SegPageSortContext,
    RiskRegisterViewState,
    serializeViewColumns,
    sortToSettings,
    isGroupHeaderInCodeColumn,
    toDisplayedColumns,
    toRowColumns,
    toSegPageSortParams,
    viewModeFromName,
    viewModeName,
    writePageSettings,
} from "./risk-register-view.util";
import {
    buildFilterChips,
    buildFilterFields,
    formatThresholdRange,
    fromColumnFilter,
    omitDisabledFilters,
    RISK_REGISTER_RANGE_OPERATORS,
    RiskRegisterFilterChip,
    RiskRegisterFilterFieldDef,
    RiskRegisterFilterKind,
    RiskRegisterFilterRow,
    RiskRegisterFilterState,
    rowOperatorLabel,
    sanitizeFilterState,
    toColumnFilter,
    toRequestFilters,
    withFilterOperator,
} from "./risk-register-filter.util";
import { ColumnFilter, FilterOperator } from "src/app/models/columnFilter.model";
import {
    applyRefreshedRows,
    buildFlatDisplayRows,
    buildGroupedDisplayRows,
    hasOpenRowsAhead,
    hasSameGroups,
    hasSamePairs,
    mergeGroupedRows,
} from "./risk-register-grouping.util";
import { RiskRegisterGroupBy } from "src/app/enums/riskRegisterGroupBy.enum";
import { RiskRegisterDisplayRowKind } from "src/app/enums/riskRegisterDisplayRowKind.enum";
import {
    RiskRegisterDisplayRow,
    RiskRegisterGroupedRowResponse,
    RiskRegisterGroupResponse,
} from "src/app/models/risk-register-grouping.model";
import { buildBulkUpdateRequest, mergeBulkUpdateResponses, summarizeBulkUpdate, summarizeColumnDelete } from "./risk-register-bulk-update.util";
import {
    addExplicit,
    buildBulkUpdateTargets,
    countSelection,
    deselectRegisters,
    editableStatusFilter,
    emptySelection,
    isColumnFullySelected,
    isRegisterSelected,
    restrictSelection,
    restrictToEditable,
    RiskRegisterEditableSelection,
    RiskRegisterSelection,
    RiskRegisterSelectionEntry,
    RiskRegisterSelectionScope,
    selectAll,
    setColumnSelected,
    setRegisterSelected,
} from "./risk-register-selection.util";
import { RiskRegisterBulkDialogType } from "src/app/enums/riskRegisterBulkDialogType.enum";
import {
    RiskRegisterBulkEdit,
    RiskRegisterBulkOutcome,
    RiskRegisterBulkResultDialogData,
    RiskRegisterBulkSendOptions,
    RiskRegisterBulkSentContext,
    RiskRegisterBulkSummary,
    RiskRegisterBulkTarget,
    RiskRegisterBulkUpdateResponse,
} from "src/app/models/risk-register-bulk-update.model";
import {
    RiskRegisterAgentColumnDeleteResponse,
    RiskRegisterAgentColumnResponse,
    RiskRegisterLabels,
    RiskRegisterMatrixColumn,
    RiskRegisterSegPageResponse,
    RiskRegisterSegRef,
    SegPageSortParams,
} from "src/app/models/risk-register-seg-page.model";
import { chunkArray } from "src/app/shared/utils/array.util";
import { RiskRegisterBulkResultDialogComponent } from "src/app/modals/risk-register-bulk-result-dialog/risk-register-bulk-result-dialog.component";

import { trackByAgentId, trackById, trackByIndex } from "src/app/shared/utils/track-by.util";
import { SampleSurveyTypeService } from "src/app/services/sample-survey-type.service";
import { SampleFilterOption } from "src/app/common-components/sample-filter-dimension/sample-filter-dimension.component";
import { buildSampleFilterSummaryRows } from "src/app/shared/utils/sample-filter.util";
import {
    HazardCategory,
    HAZARD_CATEGORY_DISPLAY_LABELS,
    HAZARD_CATEGORY_ORDER,
} from "src/app/enums/hazardCategory.enum";
import { RiskRegisterGenerationStatus } from "src/app/enums/riskRegisterGenerationStatus.enum";
import { RiskAssessmentProgress } from "src/app/models/risk-assessment-progress.model";
import {
    RiskRegisterCapabilityProfile,
    STANDARD_CAPABILITY_PROFILE,
    getRiskRegisterCapabilityProfile,
} from "./risk-register-capability-profile";
import { ComplianceStateDisplayMap, ComplianceStateEnum } from "src/app/enums/complianceState.enum";
import { RiskRegisterCardColorScale } from "src/app/enums/riskRegisterCardSlot.enum";
import {
    RiskRegisterCardSlotKey,
    RiskRegisterCardSlotPickerData,
    RiskRegisterCardSlotPickerResult,
    RiskRegisterCardSlots,
} from "src/app/models/risk-register-card.model";
import { RiskRegisterCardSlotPickerComponent } from "src/app/modals/risk-register-card-slot-picker/risk-register-card-slot-picker.component";
import {
    buildRegisterCard,
    cardColorScalesInUse,
    cardSlotsFromPageSettings,
    cardSlotsPageSettingsPatch,
    defaultCardSlots,
    editableCardSlotKeys,
    pickCardPreviewSample,
    slotOptionForViewMode,
    translateCardSlotOptionLabels,
    viewModeForSlotOption,
    writeCardSlotsPageSettings,
} from "./risk-register-card-slots.util";

const ARCHIVED_NOTE = "The registers are archived and no longer shown. Their sample links, control assignments and Bayesian results are kept.";

@Component({
  standalone: false,
    selector: "app-risk-register",
    templateUrl: "./risk-register.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./risk-register.component.scss",
})
export class RiskRegisterComponent implements OnInit, OnDestroy {
    private viewport?: CdkVirtualScrollViewport;
    private viewportResizeObserver?: ResizeObserver;
    private readonly viewportChanged$ = new Subject<void>();

    @ViewChild(CdkVirtualScrollViewport)
    set viewportRef(viewport: CdkVirtualScrollViewport | undefined) {
        this.viewportChanged$.next();
        this.viewportResizeObserver?.disconnect();
        this.viewport = viewport;
        if (!viewport) {
            return;
        }
        viewport.renderedRangeStream
            .pipe(
                filter((range) => this.isNearEnd(range) || this.nextPageFailed),
                takeUntil(this.viewportChanged$),
                takeUntil(this.destroy$),
            )
            .subscribe((range) => this.ngZone.run(() => this.onRenderedRangeChange(range)));
        viewport
            .elementScrolled()
            .pipe(takeUntil(this.viewportChanged$), takeUntil(this.destroy$))
            .subscribe(() => this.onViewportScrolled());
        this.viewportResizeObserver = new ResizeObserver(() => {
            viewport.checkViewportSize();
            this.measureAgentColumns();
        });
        this.viewportResizeObserver.observe(viewport.elementRef.nativeElement);
        this.scheduleAgentColumnMeasure();
    }

    readonly renderedColumns = signal<ListRange>({ start: 0, end: RiskRegisterComponent.INITIAL_RENDERED_COLUMNS });
    readonly agentSlots = signal<number[]>([]);
    readonly rowColumns = signal<string[]>([]);
    readonly rowLayoutKey = computed(() => this.rowColumns().join("|"));
    private agentStartPx = 0;
    private agentColumnWidthPx = 0;
    private frozenWidthPx = 0;
    private lastScrollLeft = 0;

    @ViewChild("changeSelectionDialog")
    changeSelectionDialog!: TemplateRef<any>;

    @ViewChild("agentsTab")
    agentsTab!: TemplateRef<unknown>;
    @ViewChild("quickFiltersTab")
    quickFiltersTab!: TemplateRef<unknown>;
    @ViewChild("groupByMenu")
    groupByMenu!: MatMenu;
    @ViewChild("search")
    private searchInputRef?: ElementRef<HTMLInputElement>;

    assessmentId: string;
    readonly viewObjectId = RISK_REGISTER_VIEW_OBJECT_ID;
    gridId = riskRegisterGridId(null);
    readonly refreshViews$ = new Subject<void>();
    defaultGridViewId?: string;
    currentSelectedViewId?: string;
    selectedGridView: GridView | null = null;
    viewTitle = "";
    viewColumns: DisplayGridColumnSettings[] = buildViewColumns(null);
    frozenColumnCount = RISK_REGISTER_DEFAULT_FROZEN_COLUMN_COUNT;
    frozenColumnNames: ReadonlySet<string> = new Set();
    freezableColumnNames: ReadonlySet<string> = new Set();
    isViewFlyoutOpen = false;
    flyoutTabs: GridSettingsExtraTab[] = [];
    agentToggles: RiskRegisterAgentToggle[] = [];
    filteredAgentToggles: RiskRegisterAgentToggle[] = [];
    agentSearch = "";
    allAgentsVisible = true;
    hiddenAgentIds: ReadonlySet<string> = new Set();
    allAgentColumns: RiskRegisterMatrixColumn[] = [];
    matrixSort: RiskRegisterMatrixSort | null = null;
    canReorderColumns = false;
    canEditRegisters = false;
    columnReorderEnabled = false;
    hasCustomColumnOrder = false;
    isSavingColumnOrder = false;
    private viewBackup: GridView | null = null;
    private viewStateAtFlyoutOpen: string | null = null;
    private savedViewStateKey: string | null = null;
    hasUnsavedViewChanges = false;
    private pendingNamingFlow: Subject<GridView | undefined> | null = null;
    private serverRegisterCount = 0;
    displayedColumns: string[] = [];
    groupHeaderInCodeColumn = true;
    dataSource: TableExtendedRiskRegister[] = [];
    displayRows: RiskRegisterDisplayRow[] = [];
    private collapsedGroupKeys: ReadonlySet<string> = new Set();
    readonly rowKind = RiskRegisterDisplayRowKind;
    readonly groupByOptions = [
        { value: RiskRegisterGroupBy.None, label: "None" },
        { value: RiskRegisterGroupBy.OrganizationLevel, label: "Organization Level" },
    ];
    groupBy = RiskRegisterGroupBy.None;
    private groupedRows: RiskRegisterGroupedRowResponse[] = [];
    private groups: RiskRegisterGroupResponse[] = [];
    private rowCount = 0;
    segCount = 0;
    registerCount = 0;
    isLoadingNextPage = false;
    isExporting = false;
    rowHeightPx = RiskRegisterComponent.ROW_HEIGHT_PX;
    private nextPage = 1;
    private nextPageFailed = false;
    private detailsSegId: string | null = null;
    private selection: RiskRegisterSelection = emptySelection();
    private readonly reset$ = new Subject<void>();
    private readonly viewLoad$ = new Subject<void>();
    private columnOrderEpoch = 0;
    private readonly searchInput$ = new Subject<string>();
    columnDeleteDisabled = false;
    columnDeleteTooltip = "";
    rowDeleteTooltip = "";
    dragHandleTooltip = "";
    consequenceRatingLabel = "";
    readonly permissionObject = RolePermissionObjects.RiskRegister;
    readonly deleteAction = GridPermissions.Delete;
    assessmentStatusEnum = AssessmentStatusEnum;
    isParentAssessmentApproved = false;
    // Async register-generation state. While Queued/Running (isGenerating) or Failed the register matrix
    // and its actions are hidden — the registers do not exist yet — and a status panel is shown instead.
    generationStatusEnum = RiskRegisterGenerationStatus;
    generationStatus: RiskRegisterGenerationStatus | undefined;
    generationError: string | null = null;
    isGenerating = false;
    isGenerationFailed = false;
    generationTimedOut = false;
    registerContentVisible = true;
    private readonly destroy$ = new Subject<void>();
    private pollDelayMs = RiskRegisterComponent.POLL_INITIAL_DELAY_MS;
    private pollStartedAt = 0;
    private consecutivePollFailures = 0;
    private static readonly SEG_PAGE_SIZE = 50;
    private static readonly PAGE_FETCH_CONCURRENCY = 4;
    private static readonly NO_ORG_LEVEL_LABEL = "No organization level";
    private static readonly SEARCH_DEBOUNCE_MS = 300;
    private static readonly NEXT_PAGE_THRESHOLD_ROWS = 25;
    private static readonly INITIAL_RENDERED_COLUMNS = 12;
    private static readonly RENDERED_COLUMN_BUFFER = 1;
    static readonly COMPACT_TOOLBAR_QUERY = "(max-width: 1599.98px)";
    static readonly SHORT_SCREEN_QUERY = "(max-height: 900px)";
    static readonly ROW_HEIGHT_PX = 68;
    static readonly SHORT_SCREEN_ROW_HEIGHT_PX = 60;
    private static readonly POLL_INITIAL_DELAY_MS = 2000;
    private static readonly POLL_MAX_DELAY_MS = 10000;
    private static readonly POLL_BACKOFF_FACTOR = 1.5;
    private static readonly POLL_OVERALL_CAP_MS = 15 * 60 * 1000;
    private static readonly POLL_MAX_CONSECUTIVE_FAILURES = 5;
    showDetails: boolean;
    selectedRiskId: string;
    extraColumns: RiskRegisterMatrixColumn[] = [];
    riskLevels: RiskLevel[] = [];
    exposureLikelihoodLevels: LikelihoodColorLevel[] = [];
    consequenceRatings: ConsequenceRating[] = [];
    // DASH-3171: %OEL band scale (likelihood levels) + health-risk-level legends shown at the top of
    // the register so the bands a register is read against are always visible, incl. qualitative
    // registers. Pre-computed (never a template method call) whenever the resolved config changes.
    likelihoodLegendItems: RiskLegendItem[] = [];
    riskLevelLegendItems: RiskLegendItem[] = [];
    riskAssessment: RiskAssessment;
    surveyTypeOptions: SampleFilterOption[] = [];
    sampleFilterSummaryRows: { label: string; value: string }[] = [];
    // G19/DASH-2977 (FE-3, AC8): English label of the non-detect handling method SNAPSHOTTED onto
    // the registers at calculation time (i.e. the method the displayed statistics were computed
    // under), not the jurisdiction's current setting. Empty => hide the meta-item.
    snapshotCensoredDataMethodLabel: string = '';
    currentSearchQuery: string = '';
    assessmentMethodEnum = AssessmentMethodEnum;
    isLoading: boolean;
    bulkLoading: boolean;
    bulkMode = false;
    isAllSelected: boolean;
    selectedCount: number = 0;
    // DASH-3168: one tab per hazard category present in the assessment (canonical order). The active
    // tab selects a capability profile that drives the grid's behaviour, replacing the old
    // isPsychologicalHazard boolean and its ~20 branches.
    hazardCategoryTabs: HazardCategory[] = [];
    selectedCategory: HazardCategory | null = null;
    activeProfile: RiskRegisterCapabilityProfile = STANDARD_CAPABILITY_PROFILE;
    readonly hazardCategoryLabels = HAZARD_CATEGORY_DISPLAY_LABELS;
    viewMode: RiskRegisterViewMode = RiskRegisterViewMode.HealthRisk;
    viewModeEnum = RiskRegisterViewMode;
    cardSlots: RiskRegisterCardSlots = defaultCardSlots(this.activeProfile, this.viewMode);
    private readonly cardSlotsByCategory = new Map<HazardCategory, RiskRegisterCardSlots>();
    editableCardSlots: RiskRegisterCardSlotKey[] = editableCardSlotKeys(this.activeProfile);
    showLikelihoodLegend = true;
    showRiskLevelLegend = true;
    showComplianceLegend = false;
    complianceLegendItems: RiskLegendItem[] = [];

    dialogRef?: MatDialogRef<any>;
    dialogTitle = "";
    dialogOptions: SelectableLookup[] = [];
    selectedDialogValue: number | null | undefined;
    initialDialogValue: number | null | undefined;
    selectedControlIds: (number | string)[] = [];
    isDialogConfirmDisabled = true;
    dialogType: RiskRegisterBulkDialogType | null = null;
    readonly dialogTypeEnum = RiskRegisterBulkDialogType;
    singleRegisterId: string | null = null;
    singleRegisterStatus: RiskRegisterAssessmentStatusEnum | null = null;
    singleRegisterLocked = false;
    readonly approvedBlockedText = APPROVED_REGISTER_BLOCKED_TEXT;
    readonly approvedBulkHintText = APPROVED_REGISTER_BULK_HINT_TEXT;
    bulkUpdatingCount = 0;
    bulkComment = "";
    isBulkCommentValid = false;
    readonly bulkCommentMaxLength = 1000;
    allowedStatusList: ILookup[];
    selectedRegisterStatus: RiskRegisterAssessmentStatusEnum = null;
    riskRegisterAssessmentStatusEnum = RiskRegisterAssessmentStatusEnum;
    registerStatusColors = RiskRegisterAssessmentColorsEnum;
    isFilterOpen: boolean;
    additionalFilters: RiskRegisterFilterState = {};
    appliedFilterTags: RiskRegisterFilterChip[] = [];
    filterFields: RiskRegisterFilterFieldDef[] = [];
    filterRows: RiskRegisterFilterRow[] = [];
    disabledFilterKeys: ReadonlySet<string> = new Set();
    editingFilterKey: string | null = null;
    filterSummaries: Record<string, { operator: string; value: string }> = {};
    availableFilterFields: RiskRegisterFilterFieldDef[] = [];
    readonly rangeOperators = RISK_REGISTER_RANGE_OPERATORS;
    readonly filterKind = RiskRegisterFilterKind;
    private readonly filtersTabKey = "quickFilters";
    readonly getFilterOptions = (filter: ColumnFilter): { value: number; name: string }[] => {
        const search = filter.search?.toLowerCase();
        const options = filter.column.enumMap ?? [];
        return search ? options.filter((option) => option.name.toLowerCase().includes(search)) : options;
    };
    oelCorrectionMethodEnum = getNamedEnum(OelCorrectionMethod);
    private jurisdictions: Lookup[] = [];
    private oelTypes: Lookup[] = [];
    qualitativeExceedanceLabel: string;
    qualitativeFrequencyLabel: string;
    compactToolbar = false;
    gridSettingsTooltip = "";

    constructor(
        private aroute: ActivatedRoute,
        private riskRegisterService: RiskRegisterService,
        private riskAssessmentService: RiskAssessmentService,
        protected dialog: MatDialog,
        protected toastService: ToastService,
        protected timeZoneService: TimeZoneService,
        private router: Router,
        private tenantRiskService: TenantRiskService,
        private commonService: CommonService,
        private translateService: TranslateService,
        private jurisdictionService: JurisdictionService,
        private oelTypeService: OelTypeService,
        private sessionPreferences: SessionPreferencesService,
        private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
        private segProfileService: SegProfileService,
        private sampleSurveyTypeService: SampleSurveyTypeService,
        private injector: Injector,
        private ngZone: NgZone,
        private gridViewService: GridViewService,
        private permissionService: PermissionService,
        private breakpointObserver: BreakpointObserver,
        private agentService: AgentService,
    ) {
        this.qualitativeExceedanceLabel = this.translateService.getDynamicTranslation('Exceedance Probability');
        this.qualitativeFrequencyLabel = this.translateService.getDynamicTranslation('Frequency of Exposure');
        this.breakpointObserver
            .observe([RiskRegisterComponent.COMPACT_TOOLBAR_QUERY, RiskRegisterComponent.SHORT_SCREEN_QUERY])
            .pipe(takeUntil(this.destroy$))
            .subscribe((state) => {
                this.compactToolbar = state.breakpoints[RiskRegisterComponent.COMPACT_TOOLBAR_QUERY] ?? false;
                this.rowHeightPx = state.breakpoints[RiskRegisterComponent.SHORT_SCREEN_QUERY]
                    ? RiskRegisterComponent.SHORT_SCREEN_ROW_HEIGHT_PX
                    : RiskRegisterComponent.ROW_HEIGHT_PX;
            });
        this.searchInput$
            .pipe(
                debounceTime(RiskRegisterComponent.SEARCH_DEBOUNCE_MS),
                map((query) => query.trim()),
                filter((query) => query !== this.currentSearchQuery),
                takeUntil(this.destroy$),
            )
            .subscribe((query) => {
                this.currentSearchQuery = query;
                if (this.registerContentVisible && this.assessmentId) {
                    this.resetAndLoad();
                }
            });
    }

    ngOnInit(): void {
        this.isLoading = true;
        this.dragHandleTooltip = this.translateService.getDynamicTranslation(
            "Drag to reorder agent columns for everyone on this assessment",
        );
        this.consequenceRatingLabel = this.translateService.getDynamicTranslation("Conseq. Rating:");
        this.viewMode = this.sessionPreferences.getRiskRegisterViewMode();
        this.restoreCardSlots();
        this.canReorderColumns = this.permissionService.hasPermissionFlag(
            RolePermissionObjects.RiskAssessment,
            gridToPermissionFlags[GridPermissions.Update],
        );
        this.canEditRegisters = this.permissionService.hasPermissionFlag(
            RolePermissionObjects.RiskRegister,
            gridToPermissionFlags[GridPermissions.Update],
        );
        this.refreshAgentLayout();
        this.updateColumnReorderState();
        const rawId = this.aroute.snapshot.paramMap.get("id");
        if (!rawId) {
            this.router.navigate([RoutePaths.riskAssessments]);
            return;
        }
        this.assessmentId = rawId;
        forkJoin({
            tenant: this.tenantRiskService.getCurrent(),
            riskAssessment: this.riskAssessmentService.getById(
                this.assessmentId,
            ),
            jurisdictions: this.jurisdictionService.getAll(""),
            oelTypes: this.oelTypeService.getAll(""),
            surveyTypes: this.sampleSurveyTypeService.getAll(),
        })
            .pipe(
                // DASH-2718 (item 2): the tenant -> jurisdiction-override -> qualitative-labels chain now
                // lives in the shared resolveRiskConfig() util, consumed by BOTH this page and the Sampling
                // Plan detail matrix so the two grids resolve identical bands/colours and cannot drift.
                switchMap((result) => {
                    this.riskAssessment = result.riskAssessment;
                    this.isParentAssessmentApproved =
                        this.riskAssessment?.assessmentStatus ===
                        AssessmentStatusEnum.Approved;
                    this.hasCustomColumnOrder = !!this.riskAssessment?.agentColumnOrder?.length;
                    this.computeColumnDeleteState();
                    this.initHazardCategoryTabs();
                    this.jurisdictions = result.jurisdictions;
                    this.oelTypes = result.oelTypes;
                    this.surveyTypeOptions = (result.surveyTypes ?? []).map(s => ({ id: s.id, name: s.name }));
                    this.computeSampleFilterSummaryRows();

                    return resolveRiskConfig(
                        result.tenant,
                        result.riskAssessment?.jurisdictionId,
                        this.jurisdictionRiskConfigService,
                        {
                            defaultColor: this.commonService.colors.greyLight,
                            defaultLabels: {
                                exceedanceLabel: this.qualitativeExceedanceLabel,
                                frequencyLabel: this.qualitativeFrequencyLabel,
                            },
                        },
                    );
                }),
            )
            .subscribe({
                next: (resolved) => {
                    this.riskLevels = resolved.riskLevels;
                    this.exposureLikelihoodLevels = resolved.exposureLikelihoodLevels;
                    this.consequenceRatings = resolved.consequenceRatings;
                    this.buildLegendItems();
                    this.qualitativeExceedanceLabel =
                        resolved.qualitativeDimensionLabels.exceedanceLabel;
                    this.qualitativeFrequencyLabel =
                        resolved.qualitativeDimensionLabels.frequencyLabel;
                    this.applyGenerationState(
                        this.riskAssessment?.generationStatus,
                        this.riskAssessment?.generationError ?? null,
                    );
                    if (this.isGenerating) {
                        this.isLoading = false;
                        this.startGenerationPolling();
                    } else if (this.isGenerationFailed) {
                        this.isLoading = false;
                    } else {
                        this.loadTabView();
                    }
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.showError(error);
                },
            });
        this.allowedStatusList = getEnumLookup(
            RiskRegisterAssessmentStatusEnum,
        ).filter((res) =>
            [
                RiskRegisterAssessmentStatusEnum.Approved,
                RiskRegisterAssessmentStatusEnum.Draft,
            ].includes(res.id),
        );
    }

    ngOnDestroy(): void {
        this.viewportResizeObserver?.disconnect();
        this.reset$.next();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private applyGenerationState(
        status: RiskRegisterGenerationStatus | undefined,
        error: string | null,
    ): void {
        this.generationStatus = status;
        this.generationError = error ?? null;
        this.isGenerating =
            status === RiskRegisterGenerationStatus.Queued ||
            status === RiskRegisterGenerationStatus.Running;
        this.isGenerationFailed = status === RiskRegisterGenerationStatus.Failed;
        if (this.isGenerating || this.isGenerationFailed) {
            this.generationTimedOut = false;
        }
        this.updateRegisterContentVisibility();
    }

    private updateRegisterContentVisibility(): void {
        this.registerContentVisible =
            !this.isGenerating && !this.isGenerationFailed && !this.generationTimedOut;
    }

    private startGenerationPolling(): void {
        this.pollStartedAt = Date.now();
        this.pollDelayMs = RiskRegisterComponent.POLL_INITIAL_DELAY_MS;
        this.consecutivePollFailures = 0;
        this.scheduleNextGenerationPoll();
    }

    private scheduleNextGenerationPoll(): void {
        timer(this.pollDelayMs)
            .pipe(
                switchMap(() =>
                    this.riskAssessmentService
                        .getProgress(this.assessmentId)
                        .pipe(catchError(() => of(null))),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (progress) => this.handleGenerationProgress(progress),
            });
    }

    private handleGenerationProgress(progress: RiskAssessmentProgress | null): void {
        if (!progress) {
            this.consecutivePollFailures++;
            if (
                this.consecutivePollFailures >=
                RiskRegisterComponent.POLL_MAX_CONSECUTIVE_FAILURES
            ) {
                this.applyGenerationState(
                    RiskRegisterGenerationStatus.Failed,
                    this.translateService.getDynamicTranslation(
                        "Lost contact with the server while generating risk registers. Please refresh to try again.",
                    ),
                );
                return;
            }
            this.rescheduleGenerationPoll();
            return;
        }

        this.consecutivePollFailures = 0;

        if (
            progress.generationStatus === RiskRegisterGenerationStatus.NotGenerated
        ) {
            this.applyGenerationState(
                RiskRegisterGenerationStatus.Failed,
                this.translateService.getDynamicTranslation(
                    "Risk register generation state reset unexpectedly. Please refresh to try again.",
                ),
            );
            return;
        }

        this.applyGenerationState(progress.generationStatus, progress.generationError);

        if (progress.generationStatus === RiskRegisterGenerationStatus.Completed) {
            this.loadTabView();
            return;
        }
        if (progress.generationStatus === RiskRegisterGenerationStatus.Failed) {
            return;
        }
        if (
            Date.now() - this.pollStartedAt >=
            RiskRegisterComponent.POLL_OVERALL_CAP_MS
        ) {
            this.isGenerating = false;
            this.generationTimedOut = true;
            this.updateRegisterContentVisibility();
            return;
        }
        this.rescheduleGenerationPoll();
    }

    private rescheduleGenerationPoll(): void {
        this.pollDelayMs = Math.min(
            this.pollDelayMs * RiskRegisterComponent.POLL_BACKOFF_FACTOR,
            RiskRegisterComponent.POLL_MAX_DELAY_MS,
        );
        this.scheduleNextGenerationPoll();
    }

    keepCheckingGeneration(): void {
        this.generationTimedOut = false;
        this.isGenerating = true;
        this.updateRegisterContentVisibility();
        this.startGenerationPolling();
    }

    // No backend regenerate endpoint exists yet, so a failed generation is retried by deleting the
    // assessment and creating a new one. Deleting here routes back to the list to create a fresh one.
    retryGeneration(): void {
        this.dialog
            .open(DeleteDialog, {
                data: {
                    id: this.assessmentId,
                    service: this.riskAssessmentService,
                    title: "Risk Assessment",
                    confirmationMessage: this.translateService.getDynamicTranslation(
                        "Register generation failed for this assessment. Delete it so you can create a new one? This action cannot be undone.",
                    ),
                },
                width: "440px",
                disableClose: true,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((deleted) => {
                if (deleted) {
                    this.router.navigate([RoutePaths.riskAssessments]);
                }
            });
    }

    private buildFilterBy(extra: Record<string, unknown> = {}): string {
        const filterBy: Record<string, unknown> = {};
        if (this.selectedCategory != null) {
            filterBy.OperatorTypeEnum = JSON.stringify({
                Condition: OperatorTypeEnum.Equal.toString(),
                Field: "Agent.HazardCategory",
                Value: this.selectedCategory,
            });
        }
        return JSON.stringify({ ...toRequestFilters(this.activeFilters(), this.usesExposureBandKey), ...filterBy, ...extra });
    }

    private fetchSegPage(
        page: number,
        filterBy: string,
        search: string,
        pageSize = RiskRegisterComponent.SEG_PAGE_SIZE,
        sort: SegPageSortParams = this.currentSortParams(),
    ) {
        return this.riskAssessmentService.getRiskRegisterPage(this.assessmentId, {
            page,
            pageSize,
            search,
            filterBy,
            ...sort,
            ...(this.isGrouped ? { groupBy: this.groupBy } : {}),
        });
    }

    private currentSortParams(): SegPageSortParams {
        return toSegPageSortParams(this.matrixSort, this.sortContext(new Set(this.extraColumns.map((column) => column.agentId))));
    }

    private sortContext(visibleAgentIds: ReadonlySet<string>): SegPageSortContext {
        return { viewMode: this.viewMode, usesHealthRiskScore: this.activeProfile.usesHealthRiskScore, visibleAgentIds };
    }

    private pageRowInfo(page: RiskRegisterSegPageResponse): SegPageRowInfo {
        return { segHeadcounts: page.segHeadcounts, segOrgLevels: page.segOrgLevels };
    }

    private get isGrouped(): boolean {
        return this.groupBy === RiskRegisterGroupBy.OrganizationLevel;
    }

    private pagedTotal(segCount: number, rowCount: number | undefined): number {
        return this.isGrouped ? (rowCount ?? 0) : segCount;
    }

    private get noOrgLevelLabel(): string {
        return this.translateService.getDynamicTranslation(RiskRegisterComponent.NO_ORG_LEVEL_LABEL);
    }

    private rebuildDisplayRows(): void {
        this.displayRows = this.isGrouped
            ? buildGroupedDisplayRows(this.dataSource, this.groupedRows, this.groups, this.noOrgLevelLabel, this.displayRows, this.collapsedGroupKeys)
            : buildFlatDisplayRows(this.dataSource, this.displayRows);
    }

    toggleGroup(groupKey: string): void {
        const next = new Set(this.collapsedGroupKeys);
        if (!next.delete(groupKey)) {
            next.add(groupKey);
        }
        this.collapsedGroupKeys = next;
        this.rebuildDisplayRows();
        this.loadNextPageIfNotScrollable();
    }

    resetAndLoad(preserve?: RiskRegisterSelection, onSettled?: () => void): void {
        this.reset$.next();
        this.isLoading = true;
        this.dataSource = [];
        this.groupedRows = [];
        this.groups = [];
        this.rowCount = 0;
        this.rebuildDisplayRows();
        this.viewport?.scrollToIndex(0);
        this.segCount = 0;
        this.registerCount = 0;
        this.nextPage = 1;
        this.nextPageFailed = false;
        this.isLoadingNextPage = false;
        this.selection = preserve ?? emptySelection();
        const filterBy = this.buildFilterBy();
        const search = this.currentSearchQuery;
        const orderEpoch = this.columnOrderEpoch;
        const agents$ = this.getAgentColumns(search, filterBy);
        let agentSortParams: SegPageSortParams = {};
        const load$ = isAgentColumnSort(this.matrixSort)
            ? agents$.pipe(
                  tap((agents) => {
                      agentSortParams = this.resolveSortParams(agents);
                  }),
                  switchMap((agents) =>
                      this.fetchSegPage(0, filterBy, search, RiskRegisterComponent.SEG_PAGE_SIZE, agentSortParams).pipe(
                          map((page) => ({ agents, page })),
                      ),
                  ),
              )
            : forkJoin({ agents: agents$, page: this.fetchSegPage(0, filterBy, search) });
        load$
            .pipe(
                takeUntil(this.reset$),
                takeUntil(this.destroy$),
                finalize(() => onSettled?.()),
            )
            .subscribe({
                next: ({ agents, page }) => {
                    this.setColumns(orderEpoch === this.columnOrderEpoch ? agents : orderAgentsLike(agents, this.allAgentIds()));
                    const registers = page.data ?? [];
                    this.computeSnapshotCensoredDataMethodLabel(registers);
                    this.dataSource = buildSegRows(
                        registers,
                        this.allAgentColumns,
                        this.rowRegisterMapper(),
                        this.hiddenAgentIds,
                        this.pageRowInfo(page),
                    );
                    this.groupedRows = page.rows ?? [];
                    this.groups = page.groups ?? [];
                    this.rowCount = page.rowCount ?? 0;
                    this.rebuildDisplayRows();
                    this.segCount = page.segCount;
                    this.serverRegisterCount = page.registerCount;
                    this.registerCount = this.computeRegisterCount();
                    this.nextPage = 1;
                    this.applySelectionToRows(this.dataSource);
                    this.updateSelectedCount();
                    this.isLoading = false;
                    this.loadNextPageIfNotScrollable();
                },
                error: (error) => {
                    this.isLoading = false;
                    this.toastService.showError(error);
                },
            });
    }

    private resolveSortParams(agents: RiskRegisterAgentColumnResponse[]): SegPageSortParams {
        const known = new Set(
            visibleAgentColumns(toAgentColumns(agents), this.hiddenAgentIds).map((column) => column.agentId),
        );
        const resolved = resolveSort(this.matrixSort, known);
        if (resolved !== this.matrixSort) {
            const wasSaved = !this.hasUnsavedViewChanges;
            this.matrixSort = null;
            this.syncViewState();
            if (wasSaved) {
                this.markViewStateSaved();
            }
        }
        return toSegPageSortParams(resolved, this.sortContext(known));
    }

    // The header shows each agent's default consequence rating (Agent.ConsequenceRating), not one derived from the
    // registers in the column, which are edited per register. One Agent lookup per load; if it fails (for example a
    // role without Agent access) the header shows "-" rather than failing the grid.
    private getAgentColumns(search: string, filterBy: string): Observable<RiskRegisterAgentColumnResponse[]> {
        return this.riskAssessmentService.getRiskRegisterAgents(this.assessmentId, search, filterBy).pipe(
            switchMap((agents) =>
                this.agentService.getByIds(agents.map((agent) => agent.id)).pipe(
                    catchError(() => of<Agent[]>([])),
                    map((defaults) => {
                        const defaultRatings = new Map(
                            defaults.map((agent) => [agent.id, agent.consequenceRating ?? null]),
                        );
                        return agents.map((agent) => ({
                            ...agent,
                            consequenceRating: defaultRatings.get(agent.id) ?? null,
                        }));
                    }),
                ),
            ),
        );
    }

    private setColumns(agents: RiskRegisterAgentColumnResponse[]): void {
        this.allAgentColumns = toAgentColumns(agents);
        this.rebuildColumnState();
    }

    private rebuildColumnState(): void {
        this.extraColumns = visibleAgentColumns(this.allAgentColumns, this.hiddenAgentIds);
        this.displayedColumns = toDisplayedColumns(
            this.viewColumns,
            this.extraColumns.map((column) => column.headerName),
        );
        this.agentToggles = this.allAgentColumns.map((column) => ({
            agentId: column.agentId,
            agentName: column.agentName,
            visible: !this.hiddenAgentIds.has(column.agentId),
        }));
        this.filterAgentToggles();
        this.renderedColumns.update(({ start, end }) => ({
            start: Math.min(start, this.extraColumns.length),
            end: Math.min(end, this.extraColumns.length),
        }));
        this.updateRowColumns();
        this.scheduleAgentColumnMeasure();
    }

    private updateRowColumns(): void {
        const { start, end } = this.renderedColumns();
        const slotCount = end - start;
        if (slotCount !== this.agentSlots().length) {
            this.agentSlots.set(Array.from({ length: slotCount }, (_, slot) => slot));
        }
        this.rowColumns.set(
            toRowColumns(
                this.displayedColumns,
                this.extraColumns.map((column) => column.headerName),
            ),
        );
        this.groupHeaderInCodeColumn = isGroupHeaderInCodeColumn(this.displayedColumns);
    }

    private computeRegisterCount(): number {
        return this.extraColumns.length === this.allAgentColumns.length
            ? this.serverRegisterCount
            : this.extraColumns.reduce((sum, column) => sum + column.registerCount, 0);
    }

    private scheduleAgentColumnMeasure(): void {
        afterNextRender(() => this.measureAgentColumns(), { injector: this.injector });
    }

    private measureAgentColumns(): void {
        const header = this.viewport?.elementRef.nativeElement.querySelector<HTMLElement>("th.col-agent");
        if (!header || !header.offsetWidth) {
            return;
        }
        const root = this.viewport?.elementRef.nativeElement;
        this.agentStartPx = header.offsetLeft;
        this.agentColumnWidthPx = header.offsetWidth;
        this.frozenWidthPx = [...this.frozenColumnNames].reduce(
            (sum, name) => sum + (root?.querySelector<HTMLElement>(`th.mat-column-${name}`)?.offsetWidth ?? 0),
            0,
        );
        this.updateRenderedColumns();
    }

    private onViewportScrolled(): void {
        const scrollLeft = this.viewport?.elementRef.nativeElement.scrollLeft ?? 0;
        if (scrollLeft === this.lastScrollLeft) {
            return;
        }
        this.lastScrollLeft = scrollLeft;
        this.updateRenderedColumns();
    }

    private updateRenderedColumns(): void {
        if (!this.viewport || !this.agentColumnWidthPx) {
            return;
        }
        const element = this.viewport.elementRef.nativeElement;
        const total = this.extraColumns.length;
        const visibleStart = Math.min(
            total,
            Math.floor(Math.max(0, element.scrollLeft + this.frozenWidthPx - this.agentStartPx) / this.agentColumnWidthPx),
        );
        const visibleEnd = Math.min(
            total,
            Math.max(visibleStart, Math.ceil((element.scrollLeft + element.clientWidth - this.agentStartPx) / this.agentColumnWidthPx)),
        );
        const { start, end } = this.renderedColumns();
        if (visibleStart >= start && visibleEnd <= end) {
            return;
        }
        const buffer = RiskRegisterComponent.RENDERED_COLUMN_BUFFER;
        const size = Math.min(total, Math.ceil((element.clientWidth - this.frozenWidthPx) / this.agentColumnWidthPx) + 1 + 2 * buffer);
        const windowStart = Math.max(0, Math.min(visibleStart - buffer, total - size));
        this.ngZone.run(() => {
            const sizeChanged = size !== end - start;
            this.renderedColumns.set({ start: windowStart, end: windowStart + size });
            if (sizeChanged) {
                this.updateRowColumns();
            }
        });
    }

    onRenderedRangeChange(range: ListRange): void {
        if (!this.isNearEnd(range)) {
            this.nextPageFailed = false;
            return;
        }
        this.loadNextPage();
    }

    private isNearEnd(range: ListRange): boolean {
        return this.displayRows.length - range.end <= RiskRegisterComponent.NEXT_PAGE_THRESHOLD_ROWS;
    }

    private loadNextPage(): void {
        const pageSize = RiskRegisterComponent.SEG_PAGE_SIZE;
        if (
            this.isLoading ||
            this.isLoadingNextPage ||
            this.nextPageFailed ||
            this.nextPage * pageSize >= this.pagedTotal(this.segCount, this.rowCount) ||
            (this.collapsedGroupKeys.size > 0 && this.isGrouped && !hasOpenRowsAhead(this.groups, this.nextPage * pageSize, this.collapsedGroupKeys))
        ) {
            return;
        }
        const page = this.nextPage;
        this.isLoadingNextPage = true;
        this.fetchSegPage(page, this.buildFilterBy(), this.currentSearchQuery)
            .pipe(
                takeUntil(this.reset$),
                takeUntil(this.destroy$),
                finalize(() => (this.isLoadingNextPage = false)),
            )
            .subscribe({
                next: (res) => {
                    this.computeSnapshotCensoredDataMethodLabel(res.data);
                    const incoming = buildSegRows(
                        res.data ?? [],
                        this.allAgentColumns,
                        this.rowRegisterMapper(),
                        this.hiddenAgentIds,
                        this.pageRowInfo(res),
                    );
                    this.applySelectionToRows(incoming);
                    this.dataSource = mergeSegRows(this.dataSource, incoming);
                    this.groupedRows = mergeGroupedRows(this.groupedRows, res.rows ?? []);
                    this.rebuildDisplayRows();
                    this.nextPage = page + 1;
                    this.loadNextPageIfNotScrollable();
                },
                error: (error) => {
                    this.nextPageFailed = true;
                    this.toastService.showError(error);
                },
            });
    }

    private loadNextPageIfNotScrollable(): void {
        afterNextRender(
            () => {
                if (!this.viewport) {
                    return;
                }
                const before = this.viewport.getRenderedRange();
                this.viewport.checkViewportSize();
                const after = this.viewport.getRenderedRange();
                if (before.start === after.start && before.end === after.end) {
                    this.onRenderedRangeChange(after);
                }
            },
            { injector: this.injector },
        );
    }

    private mergePageInfo(pages: RiskRegisterSegPageResponse[]): SegPageRowInfo {
        return {
            segHeadcounts: Object.assign({}, ...pages.map((page) => page.segHeadcounts)),
            segOrgLevels: Object.assign({}, ...pages.map((page) => page.segOrgLevels)),
        };
    }

    private fetchAllPages(filterBy: string, search: string, sort: SegPageSortParams): Observable<RiskRegisterSegPageResponse[]> {
        const pageSize = RiskRegisterComponent.SEG_PAGE_SIZE;
        const fetchPage = (page: number) => this.fetchSegPage(page, filterBy, search, pageSize, sort);
        return fetchPage(0).pipe(
            switchMap((first) => {
                const remaining = Math.max(0, Math.ceil(this.pagedTotal(first.segCount, first.rowCount) / pageSize) - 1);
                return from(Array.from({ length: remaining }, (_, index) => index + 1)).pipe(
                    mergeMap((page) => fetchPage(page).pipe(map((response) => ({ page, response }))), RiskRegisterComponent.PAGE_FETCH_CONCURRENCY),
                    toArray(),
                    map((rest) => [first, ...rest.sort((left, right) => left.page - right.page).map((item) => item.response)]),
                );
            }),
        );
    }

    private uniqueRegisters(pages: RiskRegisterSegPageResponse[]): RiskRegister[] {
        return [...new Map(pages.flatMap((page) => page.data ?? []).map((register) => [register.id, register])).values()];
    }

    private segIdsOfRegisters(registerIds: string[]): string[] {
        const ids = new Set(registerIds);
        return this.dataSource
            .filter((row) => (row.allRegisters ?? row.registers).some((register) => register && ids.has(register.id)))
            .map((row) => row.segId);
    }

    private registerIdsOfSegs(rows: TableExtendedRiskRegister[], segIds: ReadonlySet<string>): Set<string> {
        return new Set(
            rows
                .filter((row) => segIds.has(row.segId))
                .flatMap((row) => (row.allRegisters ?? row.registers).filter((register) => register).map((register) => register.id)),
        );
    }

    private hasSameColumns(columns: RiskRegisterMatrixColumn[]): boolean {
        return (
            columns.length === this.allAgentColumns.length &&
            columns.every((column, index) => column.agentId === this.allAgentColumns[index].agentId)
        );
    }

    private refreshSegs(segIds: string[], onSettled?: () => void): void {
        const unique = [...new Set(segIds)].filter((segId) => !!segId);
        if (!unique.length) {
            onSettled?.();
            return;
        }
        const search = this.currentSearchQuery;
        let handedToReset = false;
        forkJoin({
            agents: this.getAgentColumns(search, this.buildFilterBy()),
            pages: from(chunkArray(unique, RiskRegisterComponent.SEG_PAGE_SIZE)).pipe(
                concatMap((chunk) =>
                    this.fetchAllPages(this.buildFilterBy({ segId: chunk.join(",") }), search, {}),
                ),
                toArray(),
                map((chunks) => chunks.flat()),
            ),
            scope: this.isGrouped ? this.fetchSegPage(0, this.buildFilterBy(), search, 1) : of(null),
        })
            .pipe(
                takeUntil(this.reset$),
                takeUntil(this.destroy$),
                finalize(() => {
                    if (!handedToReset) {
                        onSettled?.();
                    }
                }),
            )
            .subscribe({
                next: ({ agents, pages, scope }) => {
                    const columns = toAgentColumns(agents);
                    const refreshed = buildSegRows(
                        this.uniqueRegisters(pages),
                        this.allAgentColumns,
                        this.rowRegisterMapper(),
                        this.hiddenAgentIds,
                        this.mergePageInfo(pages),
                    );
                    const { rows, removedSegIds } = replaceSegRows(this.dataSource, refreshed, unique);
                    const requested = new Set(unique);
                    const remaining = this.registerIdsOfSegs(rows, requested);
                    const vanished = [...this.registerIdsOfSegs(this.dataSource, requested)].filter(
                        (id) => !remaining.has(id),
                    );
                    this.selection = deselectRegisters(this.selection, new Set(vanished));
                    const refreshedPairs = pages.flatMap((page) => page.rows ?? []);
                    const pairsChanged =
                        scope !== null &&
                        (!hasSameGroups(this.rowCount, this.groups, scope.rowCount ?? 0, scope.groups ?? []) ||
                            !hasSamePairs(this.groupedRows, refreshedPairs, requested));
                    if (removedSegIds.length || pairsChanged || !this.hasSameColumns(columns)) {
                        handedToReset = true;
                        this.resetAndLoad(this.selection, onSettled);
                        return;
                    }
                    this.applyAgentColumns(columns);
                    this.dataSource = rows;
                    this.groupedRows = applyRefreshedRows(this.groupedRows, refreshedPairs);
                    this.rebuildDisplayRows();
                    this.applySelectionToRows(this.dataSource);
                    this.updateSelectedCount();
                },
                error: (error) => this.toastService.showError(error),
            });
    }

    private applyAgentColumns(columns: RiskRegisterMatrixColumn[]): void {
        const byAgent = new Map(columns.map((column) => [column.agentId, column]));
        this.allAgentColumns = this.allAgentColumns.map((column) => ({
            ...column,
            consequenceRating: byAgent.get(column.agentId)?.consequenceRating ?? null,
            registerCount: byAgent.get(column.agentId)?.registerCount ?? 0,
        }));
        this.serverRegisterCount = this.allAgentColumns.reduce((sum, column) => sum + column.registerCount, 0);
        this.rebuildColumnState();
        this.registerCount = this.computeRegisterCount();
    }

    onRegisterDetailsSaved(): void {
        if (this.detailsSegId) {
            this.refreshSegs([this.detailsSegId]);
        }
    }

    private applySelectionToRows(rows: TableExtendedRiskRegister[]): void {
        rows.forEach((row) => {
            let hasRegister = false;
            let allSelected = true;
            row.registers.forEach((register) => {
                if (!register) return;
                register.selected = isRegisterSelected(this.selection, register.id, register.agentId);
                hasRegister = true;
                allSelected = allSelected && register.selected;
            });
            row.selected = hasRegister && allSelected;
        });
        this.refreshColumnFlags();
    }

    private refreshColumnFlags(): void {
        this.extraColumns.forEach((column) => {
            column.selected = isColumnFullySelected(this.selection, column.agentId);
        });
    }

    private allAgentIds(): string[] {
        return this.allAgentColumns.map((column) => column.agentId);
    }

    private visibleAgentIds(): string[] {
        return this.extraColumns.map((column) => column.agentId);
    }

    private updateSelectedCount(): void {
        const columnCounts = new Map(this.extraColumns.map((column) => [column.agentId, column.registerCount]));
        this.selectedCount = countSelection(this.selection, columnCounts);
        this.isAllSelected = this.registerCount > 0 && this.selectedCount === this.registerCount;
    }

    private afterSelectionChange(): void {
        this.refreshColumnFlags();
        this.updateSelectedCount();
    }

    private selectionScope(): RiskRegisterSelectionScope {
        return {
            riskAssessmentId: this.assessmentId,
            filterBy: this.buildFilterBy(),
            search: this.currentSearchQuery,
            allAgentIds: this.allAgentIds(),
        };
    }

    setRegisterObject(
        register,
        labels: RiskRegisterLabels = this.registerLabels(),
        withCard = true,
        slots: RiskRegisterCardSlots = this.cardSlots,
        viewMode: RiskRegisterViewMode = this.viewMode,
    ) {
        const isHazardNotApplicable =
            register.methodOfAssessment === AssessmentMethodEnum.HazardDoesNotApply;
        const greyColor = this.commonService.colors.greyLight;
        const isHealthRisk = viewMode === RiskRegisterViewMode.HealthRisk;
        const notAvailable = labels.notAvailable;

        // Round persisted score for display; preserve null so N/A renders correctly (crit. 7).
        const healthScore =
            register?.healthRiskScore != null
                ? Math.round(register.healthRiskScore)
                : null;

        // Residual: same persisted-value rule as healthScore (AC3) — never recompute in the browser.
        const residualHealthScore =
            register?.residualHealthRisk != null
                ? Math.round(register.residualHealthRisk)
                : null;

        const rawLikelihood =
            register.methodOfAssessment === AssessmentMethodEnum.Quantitative
                ? register.quantitativeLikelihoodScore
                : register.qualitativeLikelihoodScore;

        const hasLikelihood =
            !isHazardNotApplicable &&
            rawLikelihood != null &&
            Number.isFinite(Number(rawLikelihood));

        const hasResidualLikelihood =
            register?.residualLikelihoodScore != null &&
            Number.isFinite(Number(register.residualLikelihoodScore));

        const matchedLevel = isHazardNotApplicable
            ? undefined
            : this.resolveMatchedLikelihoodLevel(register);

        const likelihoodColor = hasLikelihood
            ? getLikelihoodColor(Number(rawLikelihood), this.exposureLikelihoodLevels)
            : greyColor;
        const healthRiskColor =
            healthScore != null ? getRiskColor(healthScore, this.riskLevels) : greyColor;
        const healthRiskLevelName =
            healthScore != null ? getRiskLevelName(healthScore, this.riskLevels) : null;

        // Primary score: Health Risk view uses persisted healthRiskScore for all agent types
        // (crit. 1). Null score → neutral grey, never a colour derived from 0 (crit. 4).
        let riskColor: string;
        let displayScore: number | undefined;

        if (isHazardNotApplicable) {
            riskColor = greyColor;
            displayScore = undefined;
        } else if (isHealthRisk || this.activeProfile.usesHealthRiskScore) {
            displayScore = healthScore ?? undefined;
            riskColor = healthRiskColor;
        } else {
            riskColor = likelihoodColor;
            displayScore = hasLikelihood ? Number(rawLikelihood) : undefined;
        }

        // Residual (right-block): Health Risk view uses persisted residualHealthRisk (AC3);
        // Exposure view keeps the likelihood-based residual. Null → grey (crit. 4).
        let riskResidualColor: string;
        let residualDisplayScore: number | undefined;

        if (isHazardNotApplicable) {
            riskResidualColor = greyColor;
            residualDisplayScore = undefined;
        } else if (isHealthRisk || this.activeProfile.usesHealthRiskScore) {
            residualDisplayScore = residualHealthScore ?? undefined;
            riskResidualColor = residualHealthScore != null
                ? getRiskColor(residualHealthScore, this.riskLevels)
                : greyColor;
        } else if (!hasResidualLikelihood) {
            riskResidualColor = greyColor;
            residualDisplayScore = undefined;
        } else {
            residualDisplayScore = Number(register.residualLikelihoodScore);
            riskResidualColor = getLikelihoodColor(residualDisplayScore, this.exposureLikelihoodLevels);
        }

        // Exposure Band (crit. 8–10): shared helper — QuantitativeName only, ascending range.
        const { bandName: exposureBandDisplay } = isHazardNotApplicable
            ? { bandName: "" }
            : resolveExposureBandLabel(matchedLevel, notAvailable);

        const likelihoodNameDisplay = isHazardNotApplicable
            ? ""
            : matchedLevel?.Name?.trim() || notAvailable;

        const healthRiskRatingDisplay =
            (isHealthRisk || this.activeProfile.usesHealthRiskScore) && !isHazardNotApplicable
                ? healthRiskLevelName ?? notAvailable
                : undefined;

        const healthRiskRatingTooltip =
            (isHealthRisk || this.activeProfile.usesHealthRiskScore) && !isHazardNotApplicable
                ? labels.healthRiskRatingTooltip
                : undefined;

        const isAnalysed = hasLikelihood;

        const card = withCard
            ? buildRegisterCard(
                  slots,
                  {
                      isHazardNotApplicable,
                      exposureBand: exposureBandDisplay,
                      likelihoodName: likelihoodNameDisplay,
                      likelihoodColor,
                      healthScore,
                      healthRiskLevelName,
                      healthRiskColor,
                      complianceState: register?.complianceState,
                      consequenceRating: register?.consequenceRating,
                      residualScore: residualDisplayScore,
                      residualColor: riskResidualColor,
                      numberOfSamples: register?.quantitativeNumberOfSamples,
                      statusSource:
                          register.assessmentStatus != null
                              ? RiskRegisterAssessmentIconsEnum[register.assessmentStatus]
                              : "",
                      statusName:
                          register.assessmentStatus != null
                              ? labels.statusNames[register.assessmentStatus]
                              : "",
                      greyColor,
                  },
                  labels,
                  this.activeProfile,
              )
            : undefined;

        register = {
            ...register,
            healthRiskScore: healthScore,
            title:
                labels.methodTitles[register.methodOfAssessment as AssessmentMethodEnum] ||
                labels.methodTitles[AssessmentMethodEnum.HazardDoesNotApply],
            likelihood: hasLikelihood ? Number(rawLikelihood) : 0,
            color: riskColor,
            residualColor: riskResidualColor,
            residualDisplayScore,
            displayScore,
            healthRiskRatingDisplay,
            healthRiskRatingTooltip,
            exposureBandDisplay,
            likelihoodNameDisplay,
            isAnalysed,
            card,
        };
        return register;
    }

    // Resolves the single configured Exposure Likelihood Level that a register maps to, using the
    // register's own likelihood score. Quantitative registers match by quantitativeLikelihoodScore
    // (backend-computed, authoritative) with a percent-of-OEL threshold fallback; qualitative
    // registers match by qualitativeLikelihoodScore. Both the Exposure Band and Likelihood views
    // derive their displayed text from this level (band name vs likelihood name). Returns undefined
    // when the register is unanalysed or no configured level matches.
    private resolveMatchedLikelihoodLevel(
        register: RiskRegister,
    ): LikelihoodColorLevel | undefined {
        if (register?.methodOfAssessment === AssessmentMethodEnum.Quantitative) {
            // Match by backend-computed score first (authoritative); only fall back to
            // percent-of-OEL threshold matching when no score is available.
            const score = register?.quantitativeLikelihoodScore;
            const rawPercent = register?.quantitativeResultPercentOfOEL;
            // Guard null explicitly — Number(null) === 0 would falsely match the lowest tier.
            const quantPercent = rawPercent != null ? Number(rawPercent) : NaN;

            return (
                (score != null
                    ? this.exposureLikelihoodLevels.find((level) => level.key === score)
                    : undefined) ??
                this.exposureLikelihoodLevels.find((level) => {
                    const min = Number(level.minQuantThreshold);
                    const max = Number(level.maxQuantThreshold);
                    return (
                        Number.isFinite(min) &&
                        Number.isFinite(max) &&
                        Number.isFinite(quantPercent) &&
                        // Both bounds inclusive; first level in ascending score order wins on exact boundary.
                        quantPercent >= min &&
                        quantPercent <= max
                    );
                })
            );
        }

        const score = Number(register?.qualitativeLikelihoodScore);
        if (!Number.isFinite(score) || score === 0) {
            return undefined;
        }
        return this.exposureLikelihoodLevels.find((level) => level.key === score);
    }

    private registerLabels(): RiskRegisterLabels {
        const translated = new Map<string, string>();
        const translate = (key: string) => {
            let value = translated.get(key);
            if (value === undefined) {
                value = this.translateService.getDynamicTranslation(key);
                translated.set(key, value);
            }
            return value;
        };
        return {
            notAvailable: translate("N/A"),
            healthRiskRatingTooltip: translate("Consequence Rating x Likelihood Rating"),
            methodTitles: {
                [AssessmentMethodEnum.Quantitative]: translate("Quant"),
                [AssessmentMethodEnum.Qualitative]: translate("QUALITATIVE"),
                [AssessmentMethodEnum.HazardDoesNotApply]: translate("Hazard Does Not Apply"),
            },
            statusNames: getNamedEnum(RiskRegisterAssessmentStatusEnum),
            optionLabels: translateCardSlotOptionLabels(translate),
            translate,
        };
    }

    private rowRegisterMapper(withCard = true): (register: RiskRegister) => RiskRegister {
        const labels = this.registerLabels();
        return (register) => this.setRegisterObject(register, labels, withCard);
    }

    openRiskRegisterReportDialog() {
        if (this.isExporting) {
            return;
        }
        this.isExporting = true;
        const filterBy = this.buildFilterBy();
        const sort = this.currentSortParams();
        forkJoin({
            agents: this.getAgentColumns("", filterBy),
            pages: this.fetchAllPages(filterBy, "", sort),
            jobRollup: this.fetchSegJobRollup(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ agents, pages, jobRollup }) => {
                    const columns = toAgentColumns(agents);
                    const rows = buildSegRows(this.uniqueRegisters(pages), columns, this.rowRegisterMapper(false), new Set(), this.mergePageInfo(pages));
                    const groupedRows = this.isGrouped
                        ? buildGroupedDisplayRows(
                              rows,
                              pages.flatMap((page) => page.rows ?? []),
                              pages[0]?.groups ?? [],
                              this.noOrgLevelLabel,
                              [],
                          )
                        : undefined;
                    this.mergeJobRolesIntoRows(rows, jobRollup);
                    this.isExporting = false;
                    this.dialog.open(RiskRegistersReportPopupComponent, {
                        data: {
                            dataList: rows,
                            groupedRows,
                            agents: columns,
                            riskLevels: this.riskLevels,
                            riskAssessment: this.riskAssessment,
                            qualitativeExceedanceLabel: this.qualitativeExceedanceLabel,
                            qualitativeFrequencyLabel: this.qualitativeFrequencyLabel,
                            exposureLikelihoodLevels: this.exposureLikelihoodLevels,
                            preselectedAgentIds: this.selectedGridView?.id ? this.visibleAgentIds() : undefined,
                            keepRowOrder: Object.keys(sort).length > 0,
                        },
                        panelClass: "risk-register-report-popup",
                    });
                },
                error: (error) => {
                    this.isExporting = false;
                    this.toastService.showError(error);
                },
            });
    }

    // The register pages carry no per-SEG job rollup, so the SEG Profile report reads it from the SEG profile summary.
    // Job roles only enrich the report: an unknown org level or a failed call still opens it.
    private fetchSegJobRollup(): Observable<SegJobRollupResponse[]> {
        const orgLevelId = this.riskAssessment?.organizationLevelId;
        if (!orgLevelId) {
            return of([]);
        }
        return this.segProfileService
            .getSegProfileSummary({
                organizationLevelId: orgLevelId,
                windowStart: null,
                windowEnd: null,
                agentId: null,
            })
            .pipe(
                map((summary) => summary?.segJobRollup ?? []),
                catchError(() => of([])),
            );
    }

    /** Groups the per-SEG job rollup by segId and attaches it to each report row as `jobRoles`. */
    private mergeJobRolesIntoRows(rows: TableExtendedRiskRegister[], rollup: SegJobRollupResponse[]): void {
        const bySeg = new Map<string, SegJobRoleSource[]>();
        rollup.forEach((r) => {
            if (r.segId == null) return;
            const list = bySeg.get(r.segId) ?? [];
            list.push({ jobName: r.jobName, sampleCount: r.sampleCount });
            bySeg.set(r.segId, list);
        });
        rows.forEach((row) =>
            Object.assign(row, { jobRoles: bySeg.get(row.segId) ?? [] }),
        );
    }

    openRiskAssessmentDetails(id: string) {
        if (!this.activeProfile.opensDetailsPopup) return;
        this.selectedRiskId = id;
        this.detailsSegId = this.segIdsOfRegisters([id])[0] ?? null;
        this.showDetails = true;
    }

    searchGrid(input: { value: string }) {
        this.searchInput$.next(input.value);
    }

    hideRiskDetailFn() {
        this.selectedRiskId = null;
        this.showDetails = false;
    }

    deleteOtherRegisters(riskRegister: TableExtendedRiskRegister) {
        this.deleteRegisters(riskRegister?.allRegisters ?? riskRegister?.registers ?? []);
    }

    // DeleteDialog already showed the success toast for this row, so the fan-out only surfaces
    // failures. Each delete carries its own catchError, so one failure never aborts the rest.
    private deleteRegisters(registers: RiskRegister[]): void {
        const ids = (registers ?? [])
            .filter((register) => register?.id != undefined)
            .map((register) => register.id);
        if (!ids.length) {
            return;
        }

        this.bulkLoading = true;
        forkJoin(
            ids.map((id) =>
                this.riskRegisterService.delete(id).pipe(
                    map(() => ({ success: true })),
                    catchError(() => of({ success: false })),
                ),
            ),
        )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (results) => {
                    const failed = results.filter((r) => !r.success).length;
                    if (failed > 0) {
                        this.toastService.displayErrorMessage(
                            this.translateService.getDynamicTranslation(
                                "Failed to delete {{count}} risk register(s)",
                                { count: failed },
                            ),
                        );
                    }
                    this.bulkLoading = false;
                    this.resetAndLoad();
                },
            });
    }

    // The column delete ignores the search box and filters: the server resolves every register of the
    // agent in this assessment, so the confirmation lists exactly what the delete will archive.
    deleteColumn(column: RiskRegisterMatrixColumn): void {
        if (this.columnDeleteDisabled || this.isLoading || this.bulkLoading) {
            return;
        }

        this.isLoading = true;
        this.riskAssessmentService
            .getAgentColumnRegisters(this.assessmentId, column.agentId)
            .pipe(
                takeUntil(this.reset$),
                finalize(() => (this.isLoading = false)),
                filter((items) => items.length > 0),
                switchMap((items) => this.openColumnDeleteDialog(column, items).afterClosed()),
                filter((confirmed: boolean) => !!confirmed),
                switchMap(() => this.executeColumnDelete(column)),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (response) => this.showColumnDeleteResult(response),
                error: (error) => this.toastService.showError(error),
            });
    }

    private openColumnDeleteDialog(column: RiskRegisterMatrixColumn, items: RiskRegisterSegRef[]) {
        return this.dialog.open(BulkDeleteConfirmationDialogComponent, {
            width: "500px",
            data: {
                items,
                entityLabel: "Risk Register",
                entityLabelPlural: "Risk Registers",
                displayProperty: nameOf<RiskRegisterSegRef>((x) => x.segName),
                nameProperties: [nameOf<RiskRegisterSegRef>((x) => x.segCode)],
                subtitle: this.translateService.getDynamicTranslation("Agent: {{name}}", {
                    name: column?.agentName ?? "",
                }),
                warningNote: this.translateService.getDynamicTranslation(ARCHIVED_NOTE),
                itemsLabel: "THE FOLLOWING RISK REGISTERS WILL BE ARCHIVED:",
            } as BulkDeleteDialogData<RiskRegisterSegRef>,
            disableClose: true,
        });
    }

    private executeColumnDelete(column: RiskRegisterMatrixColumn): Observable<RiskRegisterAgentColumnDeleteResponse> {
        this.bulkLoading = true;
        return this.riskAssessmentService.deleteAgentColumnRegisters(this.assessmentId, column.agentId).pipe(
            tap({ error: () => this.resetAndLoad() }),
            finalize(() => (this.bulkLoading = false)),
        );
    }

    private showColumnDeleteResult(response: RiskRegisterAgentColumnDeleteResponse): void {
        const summary = summarizeColumnDelete(
            response,
            this.translateService.getCurrentLanguage(),
            this.translateService.getDynamicTranslation("Unknown risk register"),
        );
        if (summary.failed) {
            const data: RiskRegisterBulkResultDialogData = { summary, failedStaySelected: false, isDelete: true };
            this.dialog.open(RiskRegisterBulkResultDialogComponent, { width: "560px", data });
        } else if (response.deletedCount) {
            this.toastService.showSuccess("{{count}} risk register(s) deleted successfully", true, {
                count: response.deletedCount,
            });
        }
        this.resetAndLoad();
    }

    // The column delete is gated when the parent assessment is Approved (the backend
    // rejects the delete via EnforceParentAssessmentNotApprovedAsync, so an enabled icon only produces
    // failed requests). A column only exists when it holds at least one register.
    private computeColumnDeleteState(): void {
        this.columnDeleteDisabled = this.isParentAssessmentApproved;
        this.rowDeleteTooltip = this.isParentAssessmentApproved
            ? this.translateService.getDynamicTranslation(
                  "Registers cannot be deleted while the assessment is Approved.",
              )
            : "";
        this.columnDeleteTooltip = this.isParentAssessmentApproved
            ? this.rowDeleteTooltip
            : this.translateService.getDynamicTranslation(
                  "Delete all registers in this agent column",
              );
    }

    deleteRiskRegister(riskRegister: TableExtendedRiskRegister) {
        if (this.isParentAssessmentApproved || this.bulkLoading) {
            return;
        }
        const count = (riskRegister?.allRegisters ?? riskRegister?.registers ?? []).filter(
            (register) => register?.id != undefined,
        ).length;
        const seg = riskRegister?.segName ?? "";
        const lead =
            count === 1
                ? this.translateService.getDynamicTranslation(
                      "This will delete the risk register for {{seg}}.",
                      { seg },
                  )
                : this.translateService.getDynamicTranslation(
                      "This will delete all {{count}} risk registers for {{seg}}, across all agent columns.",
                      { count, seg },
                  );
        const permanence = this.translateService.getDynamicTranslation(ARCHIVED_NOTE);

        const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
                id: riskRegister.id,
                formDeletes: true,
                service: this.riskRegisterService,
                title: this.translateService.getDynamicTranslation(
                    "Risk Register",
                ),
                confirmationMessage: `${lead} ${permanence}`,
            },
            width: "440px",
        });
        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (confirmed: boolean) => {
                    if (confirmed) {
                        this.deleteOtherRegisters(riskRegister);
                    }
                },
            });
    }

    goToRiskRegister() {
        this.router.navigate([RoutePaths.riskAssessments]);
    }

    goBulk() {
        this.bulkMode = !this.bulkMode;
        this.updateColumnReorderState();
        if (!this.bulkMode) {
            this.selection = emptySelection();
            this.applySelectionToRows(this.dataSource);
            this.updateSelectedCount();
        }
    }

    private setSingleRegisterStatus(status: RiskRegisterAssessmentStatusEnum | null): void {
        this.singleRegisterStatus = status;
        this.singleRegisterLocked = isApprovedRiskRegisterStatus(status);
    }

    toggleRow(element: TableExtendedRiskRegister): void {
        if (this.bulkLoading) return;
        const isSelected = !!element.selected;
        element.registers.forEach((register) => {
            if (!register) return;
            register.selected = isSelected;
            this.selection = setRegisterSelected(this.selection, register.id, register.agentId, isSelected);
        });
        this.afterSelectionChange();
    }

    toggleColumn(column: RiskRegisterMatrixColumn, columnIndex: number): void {
        if (this.bulkLoading) return;
        this.selection = setColumnSelected(this.selection, column.agentId, !!column.selected);
        this.dataSource.forEach((row) => {
            const register = row.registers?.[columnIndex];
            if (!register) return;
            register.selected = isRegisterSelected(this.selection, register.id, register.agentId);
            row.selected = row.registers.every((reg) => !reg || reg.selected);
        });
        this.afterSelectionChange();
    }

    toggleSelection(row: TableExtendedRiskRegister, index: number, selected: boolean) {
        if (this.bulkLoading) return;
        const register = row.registers[index];
        register.selected = selected;
        this.selection = setRegisterSelected(this.selection, register.id, register.agentId, !!register.selected);
        row.selected = row.registers.every((reg) => !reg || reg.selected);
        this.afterSelectionChange();
    }

    toggleSelectAll(selectAllColumns: boolean) {
        if (this.bulkLoading) return;
        this.selection = selectAllColumns ? selectAll(this.visibleAgentIds()) : emptySelection();
        this.applySelectionToRows(this.dataSource);
        this.updateSelectedCount();
    }

    openDialog(type: RiskRegisterBulkDialogType) {
        this.dialogType = type;
        this.singleRegisterId = null;
        this.setSingleRegisterStatus(null);
        this.selectedDialogValue = undefined;
        this.initialDialogValue = undefined;
        this.selectedControlIds = [];
        this.bulkComment = "";
        this.isBulkCommentValid = false;

        if (type === RiskRegisterBulkDialogType.AssessmentStatus) {
            this.dialogTitle =
                this.translateService.getDynamicTranslation("Change Status");

            this.dialogOptions = this.allowedStatusList;
        } else if (type === RiskRegisterBulkDialogType.MethodOfAssessment) {
            this.dialogTitle = this.translateService.getDynamicTranslation(
                "Change Method of Assessment",
            );
            this.dialogOptions = getEnumLookup(AssessmentMethodEnum);
        } else if (type === RiskRegisterBulkDialogType.RiskRegisterControls) {
            this.dialogTitle = this.translateService.getDynamicTranslation(
                "Add Existing Control",
            );
            this.dialogOptions = [
                { id: 1, name: "Description" },
                { id: 2, name: "Sample" },
            ];
        } else if (type === RiskRegisterBulkDialogType.HealthRiskScore) {
            this.dialogTitle =
                this.translateService.getDynamicTranslation("Risk Scoring");
            this.dialogOptions = [
                {
                    id: 25,
                    name: "Requires Urgent Attention (Scored In Bottom 20%)",
                },
                { id: 20, name: "Requires Attention (Scored Below Avg)" },
                {
                    id: 15,
                    name: "Good But Could Be Improved (Scored Above Avg)",
                },
                { id: 5, name: "Excellent (Scored In Top 20%)" },
                { id: null, name: "Not Applicable" },
                { id: -1, name: "Unsure" },
            ];
        } else if (type === RiskRegisterBulkDialogType.ConsequenceRating) {
            this.dialogTitle = this.translateService.getDynamicTranslation(
                "Change Consequence Rating",
            );
            this.dialogOptions = this.consequenceRatings.map((rating) => ({
                id: rating.value,
                name: `${rating.value} - ${rating.label}`,
            }));
        }

        this.refreshDialogConfirmState();
        this.dialogRef = this.dialog.open(this.changeSelectionDialog, {
            width: "480px",
            autoFocus: false,
        });
    }

    onDialogValueChange(value: number | null) {
        this.selectedDialogValue = value;
        this.refreshDialogConfirmState();
    }

    refreshDialogConfirmState() {
        this.isDialogConfirmDisabled = this.buildBulkEdit() === null;
    }

    onDialogConfirm() {
        if (this.isDialogConfirmDisabled) return;
        this.dialogRef?.close();
        this.confirmBulkUpdate();
    }

    onDialogCancel() {
        this.dialogType = null;
        this.singleRegisterId = null;
        this.setSingleRegisterStatus(null);
        this.selectedDialogValue = null;
        this.bulkComment = "";
        this.isBulkCommentValid = false;
        this.dialogRef?.close();
    }

    onBulkCommentChange(value: string) {
        this.bulkComment = value;
        const trimmed = value?.trim() ?? "";
        this.isBulkCommentValid =
            trimmed.length > 0 && trimmed.length <= this.bulkCommentMaxLength;
        this.refreshDialogConfirmState();
    }

    confirmBulkUpdate() {
        if (this.bulkLoading) return;

        const edit = this.buildBulkEdit();
        if (!edit) return;

        if (this.singleRegisterId != null) {
            this.sendBulkUpdate(edit, [{ riskRegisterIds: [this.singleRegisterId] }], {
                updatingCount: 1,
                skippedApproved: 0,
                isSingleEdit: true,
            });
            return;
        }

        const selection = this.selection;
        const selectedCount = this.selectedCount;
        const scope = this.selectionScope();

        if (edit.type === RiskRegisterBulkDialogType.AssessmentStatus) {
            this.sendBulkUpdate(edit, buildBulkUpdateTargets(selection, scope), {
                updatingCount: selectedCount,
                skippedApproved: 0,
                isSingleEdit: false,
            });
            return;
        }

        const guardedScope = { ...scope, filterBy: this.buildFilterBy(editableStatusFilter()) };
        this.bulkLoading = true;
        defer(() => this.resolveEditableSelection(selection, guardedScope))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (editable) => {
                    this.bulkLoading = false;
                    if (editable.count === 0) {
                        this.toastService.showWarning(APPROVED_REGISTER_BLOCKED_TEXT, true);
                        return;
                    }
                    this.sendBulkUpdate(edit, buildBulkUpdateTargets(editable.selection, guardedScope), {
                        updatingCount: editable.count,
                        skippedApproved: Math.max(0, selectedCount - editable.count),
                        isSingleEdit: false,
                    });
                },
                error: (error) => {
                    this.bulkLoading = false;
                    this.toastService.showError(error);
                },
            });
    }

    private resolveEditableSelection(
        selection: RiskRegisterSelection,
        scope: RiskRegisterSelectionScope,
    ): Observable<RiskRegisterEditableSelection> {
        const statusById = new Map<string, RiskRegisterAssessmentStatusEnum>(
            this.dataSource.flatMap((row) =>
                (row.allRegisters ?? row.registers ?? [])
                    .filter((register) => register)
                    .map((register): [string, RiskRegisterAssessmentStatusEnum] => [register.id, register.assessmentStatus]),
            ),
        );
        const unknownIds = [...selection.explicit.keys(), ...selection.excluded.keys()].filter(
            (id) => !statusById.has(id),
        );
        const fetched$ = unknownIds.length ? this.riskRegisterService.getByIds(unknownIds) : of<RiskRegister[]>([]);
        const counts$ = selection.agentIds.size
            ? this.riskAssessmentService.getRiskRegisterAgents(scope.riskAssessmentId, scope.search, scope.filterBy)
            : of<RiskRegisterAgentColumnResponse[]>([]);

        return forkJoin([fetched$, counts$]).pipe(
            map(([fetched, agents]) => {
                fetched.forEach((register) => statusById.set(register.id, register.assessmentStatus));
                return restrictToEditable(
                    selection,
                    statusById,
                    new Map(agents.map((agent) => [agent.id, agent.registerCount])),
                );
            }),
        );
    }

    private sendBulkUpdate(
        edit: RiskRegisterBulkEdit,
        targets: RiskRegisterBulkTarget[],
        options: RiskRegisterBulkSendOptions,
    ) {
        if (!targets.length) return;

        const sentIds = targets.flatMap((target) => ("riskRegisterIds" in target ? target.riskRegisterIds : []));
        const hasSelector = targets.some((target) => "selector" in target);
        const expectsHealthRiskScore =
            edit.type === RiskRegisterBulkDialogType.HealthRiskScore && edit.value !== undefined;

        this.bulkUpdatingCount = options.updatingCount;
        this.bulkLoading = true;

        from(targets)
            .pipe(
                concatMap((target) =>
                    this.riskRegisterService.bulkUpdate(buildBulkUpdateRequest(target, edit)).pipe(
                        map((response): RiskRegisterBulkOutcome => ({ target, response, error: null })),
                        catchError((error) => of<RiskRegisterBulkOutcome>({ target, response: null, error })),
                    ),
                ),
                toArray(),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (outcomes) =>
                    this.onBulkOutcomes(outcomes, {
                        sentIds,
                        hasSelector,
                        isSingleEdit: options.isSingleEdit,
                        expectsHealthRiskScore,
                        skippedApproved: options.skippedApproved,
                    }),
            });
    }

    private onBulkOutcomes(outcomes: RiskRegisterBulkOutcome[], sent: RiskRegisterBulkSentContext) {
        const failedOutcomes = outcomes.filter((outcome) => !outcome.response);
        failedOutcomes.forEach((outcome) => this.toastService.showError(outcome.error));
        const responses = outcomes.flatMap((outcome) => (outcome.response ? [outcome.response] : []));
        if (!responses.length) {
            this.finishBulk(sent.sentIds, sent.hasSelector, this.selection);
            this.warnSkippedApproved(sent.skippedApproved);
            return;
        }
        const succeededTargets = outcomes.filter((outcome) => outcome.response).map((outcome) => outcome.target);
        this.onBulkUpdateCompleted(
            responses.reduce((merged, response) => mergeBulkUpdateResponses(merged, response)),
            {
                ...sent,
                sentIds: succeededTargets.flatMap((target) => ("riskRegisterIds" in target ? target.riskRegisterIds : [])),
                hasSelector: succeededTargets.some((target) => "selector" in target),
            },
            {
                selector: failedOutcomes.some((outcome) => "selector" in outcome.target),
                explicit: failedOutcomes.some((outcome) => "riskRegisterIds" in outcome.target),
            },
        );
    }

    private buildBulkEdit(): RiskRegisterBulkEdit | null {
        const value = this.selectedDialogValue;
        switch (this.dialogType) {
            case RiskRegisterBulkDialogType.AssessmentStatus:
                return value == null ? null : { type: this.dialogType, value };
            case RiskRegisterBulkDialogType.MethodOfAssessment:
                return value == null ? null : { type: this.dialogType, value };
            case RiskRegisterBulkDialogType.ConsequenceRating:
                return value == null || !this.isBulkCommentValid
                    ? null
                    : { type: this.dialogType, value, comment: this.bulkComment };
            case RiskRegisterBulkDialogType.HealthRiskScore: {
                const selectedStatus = !this.bulkMode ? this.selectedRegisterStatus : null;
                const assessmentStatus =
                    this.singleRegisterLocked && selectedStatus === this.singleRegisterStatus ? null : selectedStatus;
                const scoreChanged =
                    !this.singleRegisterLocked && value !== undefined && value !== this.initialDialogValue;
                return scoreChanged || assessmentStatus != null
                    ? { type: this.dialogType, value: scoreChanged ? value : undefined, assessmentStatus }
                    : null;
            }
            default:
                return null;
        }
    }

    private onBulkUpdateCompleted(
        response: RiskRegisterBulkUpdateResponse,
        sent: RiskRegisterBulkSentContext,
        failedTargets: { selector: boolean; explicit: boolean },
    ) {
        const summary = summarizeBulkUpdate(response, {
            sentIds: sent.sentIds,
            locale: this.translateService.getCurrentLanguage(),
            unknownLabel: this.translateService.getDynamicTranslation("Unknown risk register"),
            notChangedReason: this.translateService.getDynamicTranslation("Risk score is calculated for this agent"),
            expectsHealthRiskScore: sent.expectsHealthRiskScore,
        });

        const anyTargetFailed = failedTargets.selector || failedTargets.explicit;
        const keepFailedOnly = this.bulkMode && !sent.isSingleEdit && (summary.failed > 0 || anyTargetFailed);
        const selection = keepFailedOnly
            ? addExplicit(restrictSelection(this.selection, failedTargets), this.failedSelectionEntries(summary, response))
            : this.selection;
        this.finishBulk(sent.sentIds, sent.hasSelector, selection);

        if (summary.failed) {
            const data: RiskRegisterBulkResultDialogData = {
                summary,
                failedStaySelected: this.bulkMode && !sent.isSingleEdit,
            };
            this.dialog.open(RiskRegisterBulkResultDialogComponent, { width: "560px", data });
        } else {
            this.toastService.showSuccess("{{count}} risk register(s) updated", true, { count: summary.updated });
        }
        this.warnSkippedApproved(sent.skippedApproved);
    }

    private warnSkippedApproved(count: number) {
        if (count <= 0) return;
        this.toastService.showWarning(
            "{{count}} approved register(s) were skipped. Change their status to Draft first.",
            true,
            { count },
        );
    }

    private failedSelectionEntries(
        summary: RiskRegisterBulkSummary,
        response: RiskRegisterBulkUpdateResponse,
    ): RiskRegisterSelectionEntry[] {
        const agentIdsById = new Map((response.results ?? []).map((result) => [result.riskRegisterId, result.agentId]));
        return summary.failedIds
            .map((id) => ({ id, agentId: agentIdsById.get(id) ?? this.selection.explicit.get(id) }))
            .filter((entry): entry is RiskRegisterSelectionEntry => !!entry.agentId);
    }

    private finishBulk(sentIds: string[], hasSelector: boolean, selection: RiskRegisterSelection) {
        const settle = () => {
            this.bulkLoading = false;
            this.bulkUpdatingCount = 0;
        };
        if (hasSelector) {
            this.resetAndLoad(selection, settle);
            return;
        }
        this.selection = selection;
        this.applySelectionToRows(this.dataSource);
        this.updateSelectedCount();
        this.refreshSegs(this.segIdsOfRegisters(sentIds), settle);
    }

    onCheckboxChange(opt: SelectableLookup) {
        this.selectedControlIds = opt.selected
            ? [...new Set([...this.selectedControlIds, opt.id])]
            : this.selectedControlIds.filter((id) => id !== opt.id);
    }

    onTabChange(index: number) {
        const category = this.hazardCategoryTabs[index];
        if (category == null) return;
        this.selectedCategory = category;
        this.activeProfile = getRiskRegisterCapabilityProfile(category);
        this.restoreCardSlots();
        this.gridId = riskRegisterGridId(category);
        if (this.bulkMode) {
            this.goBulk();
        }
        this.loadTabView();
    }

    /**
     * DASH-3168: derive the tab set from the assessment's distinct hazard categories, in the
     * canonical enum order, and select the first as the active tab. No categories present (an
     * assessment with no agents) => no tabs and the standard profile.
     */
    private initHazardCategoryTabs(): void {
        const present = this.riskAssessment?.hazardCategories ?? [];
        this.hazardCategoryTabs = HAZARD_CATEGORY_ORDER.filter((category) =>
            present.includes(category),
        );
        this.selectedCategory = this.hazardCategoryTabs[0] ?? null;
        this.gridId = riskRegisterGridId(this.selectedCategory);
        this.activeProfile =
            this.selectedCategory != null
                ? getRiskRegisterCapabilityProfile(this.selectedCategory)
                : STANDARD_CAPABILITY_PROFILE;
        this.restoreCardSlots();
    }

    private restoreCardSlots(): void {
        const stored = this.selectedCategory != null ? this.cardSlotsByCategory.get(this.selectedCategory) : undefined;
        this.editableCardSlots = editableCardSlotKeys(this.activeProfile);
        this.setCardSlots(stored ?? defaultCardSlots(this.activeProfile, this.viewMode));
    }

    private applyViewCardSlots(view: GridView | null, resetStateWhenNoView: boolean): void {
        if (view) {
            this.setCardSlots(
                cardSlotsFromPageSettings(
                    view.settingsJson?.pageSettings,
                    defaultCardSlots(this.activeProfile, this.viewMode),
                    this.editableCardSlots,
                ),
            );
        } else if (resetStateWhenNoView) {
            this.restoreCardSlots();
        }
    }

    private setCardSlots(slots: RiskRegisterCardSlots): void {
        this.cardSlots = slots;
        if (this.selectedCategory != null) {
            this.cardSlotsByCategory.set(this.selectedCategory, slots);
        }
        this.syncViewModeToMain();
        this.refreshLegendVisibility();
    }

    private syncViewModeToMain(): void {
        if (this.activeProfile.usesHealthRiskScore) return;
        const mainViewMode = viewModeForSlotOption(this.cardSlots.main);
        if (mainViewMode != null && mainViewMode !== this.viewMode) {
            this.viewMode = mainViewMode;
            this.sessionPreferences.setRiskRegisterViewMode(mainViewMode);
        }
    }

    private refreshLegendVisibility(): void {
        const scales = cardColorScalesInUse(
            this.cardSlots,
            this.activeProfile,
            this.viewMode === RiskRegisterViewMode.HealthRisk || this.activeProfile.usesHealthRiskScore,
        );
        this.showLikelihoodLegend = scales.has(RiskRegisterCardColorScale.Likelihood);
        this.showRiskLevelLegend = scales.has(RiskRegisterCardColorScale.RiskLevel);
        this.showComplianceLegend = scales.has(RiskRegisterCardColorScale.Compliance);
    }

    openCardSlotPicker(): void {
        const labels = this.registerLabels();
        const sample = pickCardPreviewSample(
            this.dataSource
                .flatMap((row) => row.registers ?? [])
                .filter(
                    (register): register is RiskRegister =>
                        !!register && register.methodOfAssessment !== AssessmentMethodEnum.HazardDoesNotApply,
                ),
        );
        const data: RiskRegisterCardSlotPickerData = {
            slots: this.cardSlots,
            defaults: defaultCardSlots(this.activeProfile, this.viewMode),
            editableSlots: this.editableCardSlots,
            profile: this.activeProfile,
            preview: (slots) => {
                const viewMode = this.activeProfile.usesHealthRiskScore
                    ? this.viewMode
                    : viewModeForSlotOption(slots.main) ?? this.viewMode;
                return sample && this.setRegisterObject(sample, labels, true, slots, viewMode);
            },
        };
        this.dialog
            .open<RiskRegisterCardSlotPickerComponent, RiskRegisterCardSlotPickerData, RiskRegisterCardSlotPickerResult>(
                RiskRegisterCardSlotPickerComponent,
                { data, maxWidth: "95vw" },
            )
            .afterClosed()
            .pipe(
                filter((result): result is RiskRegisterCardSlotPickerResult => !!result),
                tap((result) => this.onCardSlotsChange(result.slots)),
                filter((result) => result.saveAsView),
                switchMap((result) =>
                    this.saveViewPageSettings(cardSlotsPageSettingsPatch(result.slots)).pipe(catchError(() => EMPTY)),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    onCardSlotsChange(slots: RiskRegisterCardSlots): void {
        const previous = this.currentViewState();
        this.setCardSlots(slots);
        this.onViewStateChanged(previous);
    }

    toggleColoringMode() {
        const toRowRegister = this.rowRegisterMapper();
        const remapped = this.dataSource.map((row) => ({
            ...row,
            allRegisters: (row.allRegisters ?? row.registers).map((register) =>
                register ? toRowRegister(register) : register,
            ),
        }));
        this.dataSource = rebuildRowCells(remapped, this.allAgentIds(), this.allAgentColumns, this.hiddenAgentIds);
        this.rebuildDisplayRows();
    }

    openHealthRiskDialog(register: RiskRegister) {
        this.openDialog(RiskRegisterBulkDialogType.HealthRiskScore);
        this.singleRegisterId = register.id;
        this.setSingleRegisterStatus(register.assessmentStatus);
        this.selectedRegisterStatus = this.allowedStatusList
            .map((res) => res.id)
            .includes(register.assessmentStatus)
            ? register.assessmentStatus
            : null;
        this.selectedDialogValue = register.healthRiskScore;
        this.initialDialogValue = register.healthRiskScore;
        this.refreshDialogConfirmState();
    }

    get jurisdictionName(): string {
        return this.jurisdictions.find(
            (j) => j.id === this.riskAssessment?.jurisdictionId,
        )?.name || "-";
    }

    get oelTypeName(): string {
        return this.oelTypes.find(
            (o) => o.id === this.riskAssessment?.oelTypeId,
        )?.name || "-";
    }

    /**
     * G19 (DASH-2976): sample-scope summary rows for the RA detail meta strip. Shares the dialog's renderer.
     * Pre-computed once after the RA + survey-type options load (see computeSampleFilterSummaryRows), rather
     * than via a template getter, so it does not re-run on every change-detection tick.
     */
    private computeSampleFilterSummaryRows(): void {
        const tt = (key: string) => this.translateService.getDynamicTranslation(key);
        this.sampleFilterSummaryRows = buildSampleFilterSummaryRows(
            this.riskAssessment?.sampleFilters,
            this.riskAssessment?.surveyTypeFilters,
            this.surveyTypeOptions,
            tt,
        );
    }

    /**
     * G19/DASH-2977 (FE-3, AC8): resolve the label for the non-detect handling method the displayed
     * statistics were computed under. All registers in a recalculated RA share the same snapshotted
     * method, so the first register that carries one is authoritative.
     */
    private computeSnapshotCensoredDataMethodLabel(registers: RiskRegister[]): void {
        const withMethod = registers?.find((r) => r?.censoredDataMethod != null);
        if (!withMethod) {
            return;
        }
        const method = withMethod.censoredDataMethod as CensoredDataMethod;
        // Use the read-only display labels, NOT the form options: the meta strip states the method the
        // statistics were actually computed under, so the form's "(default)" suffix must not leak in.
        this.snapshotCensoredDataMethodLabel =
            CENSORED_DATA_METHOD_DISPLAY_LABELS[method] ?? '';
    }

    private get usesExposureBandKey(): boolean {
        return !this.activeProfile.usesHealthRiskScore && this.viewMode === RiskRegisterViewMode.ExposureBand;
    }

    private readonly translateFilterText = (text: string): string => this.translateService.getDynamicTranslation(text);

    private buildFilterFieldDefs(): RiskRegisterFilterFieldDef[] {
        return buildFilterFields({
            profile: this.activeProfile,
            exposureBand: this.usesExposureBandKey,
            riskLevels: this.riskLevels,
            likelihoodLevels: this.exposureLikelihoodLevels,
            consequenceRatings: this.consequenceRatings,
        });
    }

    private activeFilters(): RiskRegisterFilterState {
        return omitDisabledFilters(this.additionalFilters, this.disabledFilterKeys);
    }

    private refreshFilterChips(): void {
        const chips = buildFilterChips(this.additionalFilters, this.filterFields, this.translateFilterText).map((chip) => ({
            ...chip,
            label: this.translateFilterText(chip.label),
        }));
        this.appliedFilterTags = chips.filter((chip) => !this.disabledFilterKeys.has(chip.key));
        this.filterSummaries = Object.fromEntries(
            this.filterRows.map((row) => [
                row.def.key,
                {
                    operator: this.translateFilterText(rowOperatorLabel(row)),
                    value: chips.find((chip) => chip.key === row.def.key)?.value ?? "",
                },
            ]),
        );
        this.gridSettingsTooltip = this.appliedFilterTags.length
            ? [this.translateFilterText("Grid settings"), ...this.appliedFilterTags.map((chip) => `${chip.label}: ${chip.value}`)].join("\n")
            : "";
        if (this.isViewFlyoutOpen) {
            this.flyoutTabs = this.buildFlyoutTabs();
        }
    }

    private refreshFilterView(): void {
        this.filterFields = this.buildFilterFieldDefs();
        this.additionalFilters = sanitizeFilterState(this.additionalFilters, this.filterFields);
        this.disabledFilterKeys = new Set([...this.disabledFilterKeys].filter((key) => this.additionalFilters[key]));
        this.editingFilterKey = null;
        this.filterRows = this.filterFields
            .filter((def) => this.additionalFilters[def.key])
            .map((def) => ({ def, filter: toColumnFilter(def, this.additionalFilters[def.key], this.translateFilterText) }));
        this.refreshAvailableFilterFields();
        this.refreshFilterChips();
    }

    private refreshAvailableFilterFields(): void {
        const used = new Set(this.filterRows.map((row) => row.def.key));
        this.availableFilterFields = this.filterFields.filter((def) => !used.has(def.key));
    }

    applyFilterRow(row: RiskRegisterFilterRow): void {
        const next = { ...this.additionalFilters };
        const value = fromColumnFilter(row);
        if (value) {
            next[row.def.key] = value;
        } else {
            delete next[row.def.key];
        }
        if (next[row.def.key] === this.additionalFilters[row.def.key]) {
            return;
        }
        const wasDisabled = this.disabledFilterKeys.has(row.def.key);
        this.additionalFilters = next;
        if (!value) {
            this.disabledFilterKeys = new Set([...this.disabledFilterKeys].filter((key) => key !== row.def.key));
        }
        this.refreshFilterChips();
        this.syncViewState();
        if (!wasDisabled) {
            this.resetAndLoad();
        }
    }

    addFilterRow(def: RiskRegisterFilterFieldDef): void {
        this.filterRows = [...this.filterRows, { def, filter: toColumnFilter(def, undefined, this.translateFilterText) }];
        this.editingFilterKey = def.key;
        this.refreshAvailableFilterFields();
        this.refreshFilterChips();
    }

    editFilterRow(row: RiskRegisterFilterRow): void {
        this.editingFilterKey = row.def.key;
    }

    removeFilterRow(row: RiskRegisterFilterRow): void {
        this.filterRows = this.filterRows.filter((item) => item !== row);
        if (this.editingFilterKey === row.def.key) {
            this.editingFilterKey = null;
        }
        this.refreshAvailableFilterFields();
        this.applyFilterRow({ def: row.def, filter: { ...row.filter, valueArray: [], value: undefined } });
        this.refreshFilterChips();
    }

    toggleFilterRow(row: RiskRegisterFilterRow, enabled: boolean): void {
        const next = new Set(this.disabledFilterKeys);
        if (enabled) {
            next.delete(row.def.key);
        } else {
            next.add(row.def.key);
        }
        this.disabledFilterKeys = next;
        this.refreshFilterChips();
        this.syncViewState();
        if (this.additionalFilters[row.def.key]) {
            this.resetAndLoad();
        }
    }

    onFilterOperatorChange(row: RiskRegisterFilterRow, operator: FilterOperator): void {
        const updated = withFilterOperator(row, operator);
        this.filterRows = this.filterRows.map((item) => (item === row ? updated : item));
        this.applyFilterRow(updated);
        this.refreshFilterChips();
    }

    resetFilters(): void {
        this.additionalFilters = {};
        this.disabledFilterKeys = new Set();
        this.refreshFilterView();
        this.syncViewState();
        this.resetAndLoad();
    }

    resetFiltersAndSearch(): void {
        this.currentSearchQuery = "";
        if (this.searchInputRef) {
            this.searchInputRef.nativeElement.value = "";
        }
        this.resetFilters();
    }

    /**
     * DASH-3171: build the two legends shown at the top of the register from the resolved jurisdiction
     * risk config. The likelihood legend doubles as the %OEL band scale (each band's quantitative
     * threshold range), so it renders for every register regardless of Method of Assessment — including
     * qualitative registers, where no decision statistic exists. Colours/labels are config-driven.
     */
    private buildLegendItems(): void {
        // DASH-3171 (Tandeep): show both the likelihood level name and the exposure band name as
        // `<Likelihood level name>/<Exposure band name>` on one line, with the %OEL band beneath it,
        // to keep the legend compact enough to sit beside the view-mode radios.
        this.likelihoodLegendItems = this.exposureLikelihoodLevels.map((level) => ({
            color: level.Color,
            name: [level.Name, level.QuantitativeName]
                .map((n) => (n ?? "").trim())
                .filter(Boolean)
                .join("/"),
            range: formatThresholdRange(level.minQuantThreshold, level.maxQuantThreshold, "%"),
        }));

        this.riskLevelLegendItems = this.riskLevels.map((level) => ({
            color: level.Color,
            name: (level.Name ?? "").trim(),
            range: formatThresholdRange(level.Thresholds?.[0], level.Thresholds?.[1]),
        }));

        this.complianceLegendItems = [
            ComplianceStateEnum.Compliant,
            ComplianceStateEnum.NonCompliant,
            ComplianceStateEnum.Uncertain,
        ].flatMap((state) => {
            const display = ComplianceStateDisplayMap[state];
            return display
                ? [{ color: display.color, name: this.translateService.getDynamicTranslation(display.label) }]
                : [];
        });
    }

    onGroupByChange(groupBy: RiskRegisterGroupBy): void {
        if (groupBy === this.groupBy) {
            return;
        }
        this.groupBy = groupBy;
        this.collapsedGroupKeys = new Set();
        this.syncViewState();
        this.resetAndLoad(this.selection);
    }

    matchGroupByMenuWidth(trigger: HTMLElement): void {
        const panel: HTMLElement | null = document.getElementById(this.groupByMenu.panelId);
        if (panel) {
            panel.style.minWidth = `${trigger.offsetWidth}px`;
        }
    }

    onViewModeChange(event: MatRadioChange): void {
        const previous = this.currentViewState();
        this.viewMode = event.value;
        this.sessionPreferences.setRiskRegisterViewMode(event.value);
        this.setCardSlots({ ...this.cardSlots, main: slotOptionForViewMode(event.value) });
        this.onViewStateChanged(previous);
    }

    private onViewStateChanged(previous: RiskRegisterViewState): void {
        this.refreshFilterView();
        this.syncViewState();
        this.toggleColoringMode();
        if (needsReload(previous, this.currentViewState(), new Set(this.visibleAgentIds()), !this.activeProfile.usesHealthRiskScore)) {
            this.resetAndLoad(this.selection);
        }
    }

    private viewStateKey(): string {
        return JSON.stringify({
            columns: serializeViewColumns(this.viewColumns),
            frozen: this.frozenColumnCount,
            hidden: [...this.hiddenAgentIds].sort(),
            disabledFilters: [...this.disabledFilterKeys].sort(),
            cards: this.cardSlots,
            ...this.currentViewState(),
        });
    }

    private markViewStateSaved(): void {
        this.savedViewStateKey = this.selectedGridView?.id ? this.viewStateKey() : null;
        this.hasUnsavedViewChanges = false;
    }

    private currentPageSettings(existing: Record<string, unknown> | null | undefined): Record<string, unknown> {
        return writeCardSlotsPageSettings(
            writePageSettings(existing, {
                hiddenAgentIds: this.hiddenAgentIds,
                viewMode: this.viewMode,
                groupBy: this.groupBy,
                disabledFilterKeys: this.disabledFilterKeys,
            }),
            this.cardSlots,
            defaultCardSlots(this.activeProfile, this.viewMode),
        );
    }

    private currentViewState(): RiskRegisterViewState {
        return {
            sort: this.matrixSort,
            filters: { ...this.activeFilters() },
            viewMode: viewModeName(this.viewMode),
            groupBy: this.groupBy,
        };
    }

    private updateColumnReorderState(): void {
        this.columnReorderEnabled = this.canReorderColumns && !this.bulkMode && !this.isSavingColumnOrder;
    }

    loadTabView(): void {
        this.reset$.next();
        this.viewLoad$.next();
        this.isLoading = true;
        this.gridViewService
            .getDefaultView(this.gridId)
            .pipe(
                tap({ error: () => this.toastService.displayErrorMessage("Error loading selected view", true) }),
                catchError(() => of(null)),
                takeUntil(this.viewLoad$),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (view) => {
                    this.hasUnsavedViewChanges = false;
                    this.defaultGridViewId = view?.id;
                    this.currentSelectedViewId = view?.id;
                    this.applyView(view ?? null);
                    this.resetAndLoad();
                },
            });
    }

    private applyView(view: GridView | null, resetStateWhenNoView = true): void {
        const previousGroupBy = this.groupBy;
        const viewChanged = this.selectedGridView?.id !== view?.id;
        this.selectedGridView = view;
        this.viewTitle = view?.viewName ?? "";
        const saved = view?.settingsJson?.columns;
        this.viewColumns = buildViewColumns(saved?.length ? saved : null);
        this.frozenColumnCount = saved?.length
            ? (view.settingsJson.frozenColumnCount ?? 0)
            : RISK_REGISTER_DEFAULT_FROZEN_COLUMN_COUNT;
        if (view) {
            const state = readViewState(view);
            const page = readPageSettings(view.settingsJson?.pageSettings);
            this.hiddenAgentIds = new Set(page.hiddenAgentIds);
            this.disabledFilterKeys = new Set(page.disabledFilterKeys);
            this.matrixSort = state.sort;
            this.viewMode = viewModeFromName(state.viewMode) ?? this.sessionPreferences.getRiskRegisterViewMode();
            this.additionalFilters = sanitizeFilterState(state.filters, this.buildFilterFieldDefs());
        } else {
            this.hiddenAgentIds = new Set();
            if (resetStateWhenNoView) {
                this.matrixSort = null;
                this.additionalFilters = {};
                this.disabledFilterKeys = new Set();
                this.viewMode = this.sessionPreferences.getRiskRegisterViewMode();
            }
        }
        this.applyViewCardSlots(view, resetStateWhenNoView);
        if (view) {
            this.groupBy = readViewState(view).groupBy;
        } else if (resetStateWhenNoView) {
            this.groupBy = RiskRegisterGroupBy.None;
        }
        if (viewChanged || this.groupBy !== previousGroupBy) {
            this.collapsedGroupKeys = new Set();
        }
        this.refreshFilterView();
        this.refreshAgentLayout();
        this.markViewStateSaved();
    }

    private refreshAgentLayout(): void {
        this.freezableColumnNames = freezableColumns(this.viewColumns);
        this.frozenColumnNames = frozenColumns(this.viewColumns, this.frozenColumnCount);
        this.rebuildColumnState();
        this.dataSource = rebuildRowCells(this.dataSource, this.allAgentIds(), this.allAgentColumns, this.hiddenAgentIds);
        this.rebuildDisplayRows();
        this.dropHiddenFromSelection();
        this.registerCount = this.computeRegisterCount();
        this.refreshColumnFlags();
        this.updateSelectedCount();
    }

    private dropHiddenFromSelection(): void {
        if (!this.hiddenAgentIds.size) {
            return;
        }
        const hiddenRegisterIds = new Set(
            this.dataSource
                .flatMap((row) => row.allRegisters ?? [])
                .filter((register) => register && this.hiddenAgentIds.has(register.agentId))
                .map((register) => register.id),
        );
        let selection = this.selection;
        this.hiddenAgentIds.forEach((agentId) => {
            selection = setColumnSelected(selection, agentId, false);
        });
        this.selection = deselectRegisters(selection, hiddenRegisterIds);
        this.applySelectionToRows(this.dataSource);
    }

    private syncViewState(): void {
        const settings = this.selectedGridView?.settingsJson;
        if (!settings) {
            return;
        }
        settings.sort = sortToSettings(this.matrixSort);
        settings.filters = filtersToSettings(this.additionalFilters);
        settings.frozenColumnCount = this.frozenColumnCount;
        settings.pageSettings = this.currentPageSettings(settings.pageSettings);
        if (this.savedViewStateKey !== null) {
            this.hasUnsavedViewChanges = this.viewStateKey() !== this.savedViewStateKey;
        }
    }

    private buildDraftView(): GridView {
        return {
            objectId: this.viewObjectId,
            gridId: this.gridId,
            type: GridViewType.Grid,
            viewName: this.translateService.getDynamicTranslation("New View"),
            isShared: false,
            settingsJson: {
                columns: serializeViewColumns(this.viewColumns),
                filters: filtersToSettings(this.additionalFilters),
                sort: sortToSettings(this.matrixSort),
                frozenColumnCount: this.frozenColumnCount,
                pageSettings: this.currentPageSettings(null),
            },
        };
    }

    onViewSelected(view: Lookup): void {
        if (this.selectedGridView?.id === view?.id && !this.hasUnsavedViewChanges) {
            if (this.selectedGridView && this.selectedGridView.viewName !== view.name) {
                this.selectedGridView.viewName = view.name;
                this.viewTitle = view.name;
            }
            return;
        }
        this.reset$.next();
        this.viewLoad$.next();
        this.isLoading = true;
        this.gridViewService
            .getById(String(view.id))
            .pipe(takeUntil(this.viewLoad$), takeUntil(this.destroy$))
            .subscribe({
                next: (loaded) => {
                    this.hasUnsavedViewChanges = false;
                    this.currentSelectedViewId = loaded.id;
                    this.applyView(loaded);
                    this.resetAndLoad();
                },
                error: () => {
                    this.isLoading = false;
                    this.toastService.displayErrorMessage("Error loading selected view", true);
                },
            });
    }

    onViewDeleted(viewId: string): void {
        if (this.selectedGridView?.id === viewId) {
            this.selectedGridView = null;
            this.currentSelectedViewId = undefined;
        }
    }

    onAddView(viewToDuplicate: GridView | null): void {
        this.viewStateAtFlyoutOpen = null;
        this.syncViewState();
        this.viewBackup = this.selectedGridView ? structuredClone(this.selectedGridView) : null;
        if (viewToDuplicate) {
            this.applyView(viewToDuplicate);
            this.resetAndLoad();
        } else {
            this.selectedGridView = this.buildDraftView();
            this.viewTitle = this.selectedGridView.viewName;
        }
        this.openViewFlyoutPanel();
    }

    openViewFlyout(): void {
        this.syncViewState();
        this.viewStateAtFlyoutOpen = this.viewStateKey();
        this.viewBackup = this.selectedGridView ? structuredClone(this.selectedGridView) : null;
        if (!this.selectedGridView) {
            this.selectedGridView = this.buildDraftView();
            this.viewTitle = this.selectedGridView.viewName;
        }
        this.openViewFlyoutPanel();
    }

    private openViewFlyoutPanel(): void {
        this.flyoutTabs = this.buildFlyoutTabs();
        this.isViewFlyoutOpen = true;
    }

    private buildFlyoutTabs(): GridSettingsExtraTab[] {
        return [
            {
                key: this.filtersTabKey,
                label: "Filters",
                icon: "assets/images/filter.svg",
                count: this.appliedFilterTags.length,
                template: this.quickFiltersTab,
            },
            {
                key: "agents",
                label: "Agents",
                icon: "assets/images/properties.svg",
                count: this.extraColumns.length,
                template: this.agentsTab,
            },
        ];
    }

    saveViewPageSettings(patch: Record<string, unknown>): Observable<GridView | undefined> {
        const isUpdate = !!this.selectedGridView?.id && this.selectedGridView.canManage !== false;
        this.syncViewState();
        const target = isUpdate ? this.selectedGridView! : this.buildDraftView();
        target.settingsJson.pageSettings = this.currentPageSettings({ ...target.settingsJson.pageSettings, ...patch });
        if (!isUpdate) {
            return this.openNamingFlow(target);
        }
        return saveGridView(this.gridViewService, target).pipe(
            tap({
                next: (saved) => {
                    const view = saved ?? target;
                    this.selectedGridView = view;
                    this.currentSelectedViewId = view.id;
                    this.viewTitle = view.viewName;
                    this.markViewStateSaved();
                    this.refreshViews$.next();
                    this.toastService.showSuccess("Grid view updated successfully", true);
                },
                error: () => this.toastService.showError("Error updating grid view"),
            }),
        );
    }

    private openNamingFlow(draft: GridView): Observable<GridView | undefined> {
        this.pendingNamingFlow?.complete();
        const flow = new Subject<GridView | undefined>();
        this.pendingNamingFlow = flow;
        this.viewStateAtFlyoutOpen = null;
        this.viewBackup = this.selectedGridView ? structuredClone(this.selectedGridView) : null;
        this.selectedGridView = draft;
        this.viewTitle = draft.viewName;
        this.openViewFlyoutPanel();
        return flow.asObservable();
    }

    private settleNamingFlow(created: GridView | undefined): void {
        const flow = this.pendingNamingFlow;
        this.pendingNamingFlow = null;
        flow?.next(created);
        flow?.complete();
    }

    onViewFlyoutClose(result: Lookup | null): void {
        this.isViewFlyoutOpen = false;
        const backup = this.viewBackup;
        this.viewBackup = null;
        const stateAtOpen = this.viewStateAtFlyoutOpen;
        this.viewStateAtFlyoutOpen = null;
        if (result) {
            if (this.selectedGridView && !this.selectedGridView.id && result.id) {
                this.selectedGridView.id = String(result.id);
            }
            this.currentSelectedViewId = String(result.id);
            this.viewTitle = result.name;
            this.markViewStateSaved();
            this.refreshViews$.next();
            this.settleNamingFlow(this.selectedGridView ?? undefined);
            return;
        }
        if (stateAtOpen !== null && (this.hasUnsavedViewChanges || this.viewStateKey() !== stateAtOpen)) {
            this.selectedGridView = backup;
            this.viewTitle = backup?.viewName ?? "";
            this.hasUnsavedViewChanges = true;
            return;
        }
        const before = this.currentViewState();
        this.applyView(backup, false);
        this.settleNamingFlow(undefined);
        const after = this.currentViewState();
        if (needsReload(before, after, new Set(this.visibleAgentIds()), !this.activeProfile.usesHealthRiskScore)) {
            this.resetAndLoad(this.selection);
        } else if (before.viewMode !== after.viewMode) {
            this.toggleColoringMode();
        }
    }

    onViewColumnsChanged(columns: DisplayGridColumnSettings[]): void {
        keepLockedColumnsVisible(columns);
        this.viewColumns = [...columns];
        this.refreshAgentLayout();
        this.syncViewState();
    }

    onViewFrozenColumnsChanged(count: number): void {
        this.frozenColumnCount = count;
        this.syncViewState();
        this.frozenColumnNames = frozenColumns(this.viewColumns, count);
        this.scheduleAgentColumnMeasure();
    }

    onAgentToggle(agentId: string, visible: boolean): void {
        const hidden = new Set(this.hiddenAgentIds);
        if (visible) {
            hidden.delete(agentId);
        } else {
            hidden.add(agentId);
        }
        this.applyHiddenAgents(hidden);
    }

    toggleAllAgents(visible: boolean): void {
        this.applyHiddenAgents(visible ? new Set() : new Set(this.allAgentColumns.map((column) => column.agentId)));
    }

    onAgentSearch(value: string): void {
        this.agentSearch = value;
        this.filterAgentToggles();
    }

    private filterAgentToggles(): void {
        const query = this.agentSearch.trim().toLowerCase();
        this.filteredAgentToggles = query
            ? this.agentToggles.filter((toggle) => toggle.agentName.toLowerCase().includes(query))
            : this.agentToggles;
        this.allAgentsVisible = this.agentToggles.every((toggle) => toggle.visible);
    }

    private applyHiddenAgents(hidden: ReadonlySet<string>): void {
        this.hiddenAgentIds = hidden;
        this.refreshAgentLayout();
        this.flyoutTabs = this.buildFlyoutTabs();
        const sorted = this.matrixSort?.active;
        const sortedAgentHidden = !!sorted && hidden.has(sorted);
        if (sortedAgentHidden) {
            this.matrixSort = null;
        }
        this.syncViewState();
        if (sortedAgentHidden) {
            this.resetAndLoad(this.selection);
        }
    }

    onMatrixSortChange(sort: Sort): void {
        this.matrixSort = sort.direction ? { active: sort.active, direction: sort.direction } : null;
        this.syncViewState();
        this.resetAndLoad(this.selection);
    }

    onAgentColumnDrop(event: CdkDragDrop<unknown>): void {
        if (!this.columnReorderEnabled || event.previousIndex === event.currentIndex) {
            return;
        }
        const visibleOrder = this.visibleAgentIds();
        const movedAgentId = visibleOrder[event.previousIndex];
        const reordered = [...visibleOrder];
        moveItemInArray(reordered, event.previousIndex, event.currentIndex);
        const placeAfter = event.currentIndex > 0;
        const anchorAgentId = placeAfter ? reordered[event.currentIndex - 1] : reordered[1];
        if (!movedAgentId || !anchorAgentId) {
            return;
        }
        const previousOrder = this.allAgentIds();
        this.setAgentOrder(moveInSharedOrder(previousOrder, movedAgentId, anchorAgentId, placeAfter));
        this.isSavingColumnOrder = true;
        this.updateColumnReorderState();
        this.riskAssessmentService
            .getRiskRegisterAgents(this.assessmentId, "", "")
            .pipe(
                switchMap((agents) => {
                    const sharedOrder = moveInSharedOrder(
                        agents.map((agent) => agent.id),
                        movedAgentId,
                        anchorAgentId,
                        placeAfter,
                    );
                    return this.riskAssessmentService
                        .saveAgentColumnOrder(this.assessmentId, sharedOrder)
                        .pipe(tap(() => this.markColumnOrderSaved(sharedOrder)));
                }),
                finalize(() => {
                    this.isSavingColumnOrder = false;
                    this.updateColumnReorderState();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                error: () => {
                    this.setAgentOrder(previousOrder);
                    this.showColumnOrderError();
                },
            });
    }

    resetAgentColumnOrder(): void {
        if (!this.canReorderColumns || !this.hasCustomColumnOrder || this.isSavingColumnOrder) {
            return;
        }
        this.isSavingColumnOrder = true;
        this.updateColumnReorderState();
        this.riskAssessmentService
            .saveAgentColumnOrder(this.assessmentId, [])
            .pipe(
                finalize(() => {
                    this.isSavingColumnOrder = false;
                    this.updateColumnReorderState();
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: () => {
                    this.markColumnOrderSaved([]);
                    this.resetAndLoad(this.selection);
                },
                error: () => this.showColumnOrderError(),
            });
    }

    private markColumnOrderSaved(order: string[]): void {
        this.columnOrderEpoch++;
        this.riskAssessment.agentColumnOrder = order.length ? order : null;
        this.hasCustomColumnOrder = order.length > 0;
    }

    private showColumnOrderError(): void {
        this.toastService.displayErrorMessage(
            this.translateService.getDynamicTranslation("Could not save the agent column order."),
        );
    }

    private setAgentOrder(order: string[]): void {
        const previousIds = this.allAgentIds();
        const byId = new Map(this.allAgentColumns.map((column) => [column.agentId, column]));
        const reordered = order.flatMap((agentId) => byId.get(agentId) ?? []);
        if (reordered.length !== this.allAgentColumns.length) {
            return;
        }
        this.columnOrderEpoch++;
        this.allAgentColumns = reordered;
        this.dataSource = rebuildRowCells(this.dataSource, previousIds, this.allAgentColumns, this.hiddenAgentIds);
        this.rebuildDisplayRows();
        this.rebuildColumnState();
        this.refreshColumnFlags();
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
    public trackByAgentId = trackByAgentId;
}
